import type { ResourceKey } from './resources';
import type { ScopeLevel } from './types';

//*******************************************
// Capabilities — SDD §4
//*******************************************
// Three-tier authorization model: Role → Capability → Operation.
// Operators grant capabilities (coarse, ~85 user-meaningful groupings);
// the resolver expands each capability to its `operations` list at request
// time so `route-config.json` keying stays unchanged.
//
// Every operation registered in
//   src/services/server/src/modules/identity/route-config.json
// MUST be referenced by exactly one Capabilities entry's `operations`
// array. Drift is cross-checked by `scripts/generate-capability-registry.ts`
// (TV1-1782 ticket 16). For this ticket the check was done manually — see
// the comment block at the bottom of this file for orphan analysis.
//
// `systemOwned: true` capabilities CANNOT be granted to user-defined custom
// roles (enforced in the aggregate, R2). They exist so that every operation
// in the gateway's routing surface has a defined owner — webhook handlers,
// system policies, internal sync handlers, anonymous auth endpoints, etc.
//
// ⚠️ `systemOwned` SAYS NOTHING ABOUT NETWORK EXPOSURE (TV1-1535, ADR-089 §5.1).
// It used to be called `internal`, and that name made a second, contradictory
// claim: "the routes behind this capability are unreachable from the internet".
// Exposure is decided in exactly ONE place — the route's `visibility` in
// `route-config.json`, read by `gateway.ts` Step 1.5 — and the two axes had
// drifted apart on 93 operations. The flag is not the route's opinion, and the
// two genuinely disagree: `generic:admin:infra` is `systemOwned` (no tenant
// custom role may hold outbox-drain or cronjob-tick powers) while thirteen of
// its routes are `private` on purpose, because the admin panel's own browser
// calls them.
//
// So `systemOwned` answers only: may an operator ASSIGN this capability, and
// should a client be TOLD it holds it? Its three readers are the custom-role
// validator (`validateGrants.ts` R2), the role-builder UI, and the
// `X-Auth-Capabilities` / `context.user.capabilities` disclosure filter
// (`gateway.ts` `capabilitiesToHeader`, `local-auth-middleware.ts`
// `mapAuthzClaims`). NOTHING reads it to admit or refuse a request.
//
// ESAS coordination (SDD §10): the ESAS node-graph generator (TV1-1753, in
// the pv3 repo) iterates over `Capabilities` to emit Capability nodes and
// `Capability --expands-to--> Operation` edges from each entry's `operations`
// array. Keep the shape of this object (and `resources.ts`) stable, or
// coordinate the change with TV1-1753. See the `[[esas-node-graph-project]]`
// project note. This package is the SOURCE; ESAS only reads it.

// Internal shape used as the `satisfies` constraint for `Capabilities` below.
// `implies` is typed loosely as `readonly string[]` here to avoid a circular
// reference: the publicly exported `Capability` narrows it to
// `readonly CapabilityKey[]`, which itself depends on `keyof typeof Capabilities`.
// See the `Capability` declaration after `Capabilities` for the public shape.
type CapabilityShape = {
  key: string;
  resource: ResourceKey;
  action: string;
  label: string;
  category: string;
  sensitive?: boolean;
  /**
   * System-owned: not assignable to a user-defined custom role, and hidden from
   * the client's own capability list. NOT a statement about network exposure —
   * see the note at the top of this file.
   */
  systemOwned?: boolean;
  operations: readonly string[];
  implies?: readonly string[];
};

export type Grant = {
  capability: CapabilityKey;
  scopes: ScopeLevel[];
};

export const Capabilities = {

  //*******************************************
  // Identity (user-assignable)
  //*******************************************

  'identity:users:read': {
    key: 'identity:users:read',
    resource: 'auth-users',
    action: 'read',
    label: 'Read users',
    category: 'Identity',
    operations: [
      'identity:users',
      'identity:admin-list-sessions',
      'identity:auth-user-security',
      // TV1-1968: filtered members-security `-search` route. Same read power as
      // the base `identity:auth-user-security` route — a member-security read —
      // so it belongs to the same capability, not a new one.
      'identity:auth-user-security-search',
      'identity:auth-user-security-readmodel'
    ]
  },

  'identity:users:create': {
    key: 'identity:users:create',
    resource: 'auth-users',
    action: 'create',
    label: 'Create users',
    category: 'Identity',
    operations: [
      'identity:create',
      'identity:register-auth-user',
      'identity:admin-create-admin-user',
      'identity:assign-account-membership'
    ],
    implies: ['identity:users:read']
  },

  'identity:users:update': {
    key: 'identity:users:update',
    resource: 'auth-users',
    action: 'update',
    label: 'Update users (edit, ban, roles)',
    category: 'Identity',
    operations: [
      'identity:update',
      'identity:update-auth-user',
      'identity:ban-auth-user',
      'identity:unban-auth-user',
      'identity:admin-ban-user',
      'identity:admin-unban-user',
      'identity:update-membership-roles'
    ],
    implies: ['identity:users:read']
  },

  'identity:users:delete': {
    key: 'identity:users:delete',
    resource: 'auth-users',
    action: 'delete',
    label: 'Delete users',
    category: 'Identity',
    operations: [
      'identity:remove',
      'identity:delete',
      'identity:delete-auth-user',
      'identity:remove-account-membership'
    ],
    implies: ['identity:users:read']
  },

  'identity:users:reset-password': {
    key: 'identity:users:reset-password',
    resource: 'auth-users',
    action: 'reset-password',
    label: 'Reset user password',
    category: 'Identity',
    sensitive: true,
    operations: [
      'identity:reset-password',
      'identity:forgot-password',
      'identity:change-password',
      'identity:set-password',
      'identity:admin-set-password',
      'identity:add-auth-user-password',
      'identity:change-auth-user-password',
      'identity:set-auth-user-password'
    ]
  },

  'identity:users:impersonate': {
    key: 'identity:users:impersonate',
    resource: 'auth-users',
    action: 'impersonate',
    label: 'Impersonate user',
    category: 'Identity',
    sensitive: true,
    operations: [
      'identity:admin-impersonate',
      'identity:impersonate-auth-user',
      'identity:admin-stop-impersonating',
      'identity:stop-impersonating-auth-user'
    ]
  },

  'identity:sessions:revoke': {
    key: 'identity:sessions:revoke',
    resource: 'auth-users',
    action: 'revoke-sessions',
    label: 'Revoke user sessions',
    category: 'Identity',
    operations: [
      'identity:admin-revoke-session',
      'identity:admin-revoke-all-sessions',
      'identity:revoke-auth-user-session',
      'identity:revoke-all-auth-user-sessions'
    ]
  },

  'identity:passkeys:create': {
    key: 'identity:passkeys:create',
    resource: 'auth-users',
    action: 'create-passkey',
    label: 'Register passkeys',
    category: 'Identity',
    operations: [
      'identity:register-passkey'
    ]
  },

  'identity:passkeys:delete': {
    key: 'identity:passkeys:delete',
    resource: 'auth-users',
    action: 'delete-passkey',
    label: 'Revoke passkeys',
    category: 'Identity',
    operations: [
      'identity:revoke-passkey',
      'identity:passkey-delete'
    ]
  },

  'identity:accounts:read': {
    key: 'identity:accounts:read',
    resource: 'auth-accounts',
    action: 'read',
    label: 'Read accounts',
    category: 'Identity',
    operations: [
      'identity:flags'
    ]
  },

  'identity:accounts:create': {
    key: 'identity:accounts:create',
    resource: 'auth-accounts',
    action: 'create',
    label: 'Create accounts',
    category: 'Identity',
    operations: [
      'identity:register-auth-account'
    ],
    implies: ['identity:accounts:read']
  },

  'identity:accounts:update': {
    key: 'identity:accounts:update',
    resource: 'auth-accounts',
    action: 'update',
    label: 'Update accounts',
    category: 'Identity',
    operations: [
      'identity:update-auth-account',
      'identity:set-active-account'
    ],
    implies: ['identity:accounts:read']
  },

  'identity:accounts:delete': {
    key: 'identity:accounts:delete',
    resource: 'auth-accounts',
    action: 'delete',
    label: 'Delete accounts',
    category: 'Identity',
    operations: [
      'identity:delete-auth-account'
    ],
    implies: ['identity:accounts:read']
  },

  'identity:roles:create': {
    key: 'identity:roles:create',
    resource: 'custom-roles',
    action: 'create',
    label: 'Create custom roles',
    category: 'Identity',
    sensitive: true,
    operations: [
      'identity:define-custom-role',
      'identity:role-create'
    ]
  },

  'identity:roles:update': {
    key: 'identity:roles:update',
    resource: 'custom-roles',
    action: 'update',
    label: 'Update custom roles',
    category: 'Identity',
    sensitive: true,
    operations: [
      'identity:custom-roles',
      'identity:custom-roles-readmodel',
      'identity:edit-custom-role',
      'identity:capability-registry',
      'identity:system-roles',
      'identity:role-update'
    ]
  },

  'identity:roles:delete': {
    key: 'identity:roles:delete',
    resource: 'custom-roles',
    action: 'delete',
    label: 'Delete custom roles',
    category: 'Identity',
    sensitive: true,
    operations: [
      'identity:remove-custom-role',
      'identity:role-delete'
    ]
  },

  // Baseline self-read every member holds (account-owner via `identity:*`
  // wildcard; manager / operator granted explicitly in roles-and-permissions.ts).
  // Owns the "read MY OWN identity" routes — capability projection
  // (`/identity/my-capabilities`) and security row (`/identity/auth-user-security/me`).
  // These are session-bootstrap reads, NOT grantable user-management power, so
  // they live in their own capability rather than under `identity:users:read`
  // ( which is the admin "read OTHER users" surface ). (TV1-1782 follow-up)
  'identity:self:read': {
    key: 'identity:self:read',
    resource: 'auth-users',
    action: 'read-self',
    label: 'Read own identity (capabilities + security)',
    category: 'Identity',
    operations: [
      'identity:my-capabilities',
      'identity:me'
    ]
  },

  //*******************************************
  // Clients Management (user-assignable)
  //*******************************************

  'clients-management:account:read': {
    key: 'clients-management:account:read',
    resource: 'accounts',
    action: 'read',
    label: 'Read account',
    category: 'Clients Management',
    operations: [
      'clients-management:accounts-readmodel',
      'clients-management:clients-management-configuration-readmodel',
      'clients-management:get-login-user',
      'clients-management:me',
      // TV1-2063: tenant-scoped read of the account's own suspension flag, consumed
      // in-process by the outbound side-effect gate (isAccountSuspended). Same data
      // class as `me` (which already carries suspensionStatus), so it rides under
      // account:read rather than minting a bespoke capability.
      'clients-management:suspension-status',
      // TV1-2105: tenant-scoped read of the account's own observation-mode state,
      // consumed in-process by slice-4's fail-closed egress predicate. Same data
      // class + read shape as suspension-status, so it likewise rides under
      // account:read.
      'clients-management:observation-state'
    ]
  },

  'clients-management:account:update': {
    key: 'clients-management:account:update',
    resource: 'accounts',
    action: 'update',
    label: 'Update account info',
    category: 'Clients Management',
    operations: [
      'clients-management:update-account-info',
      'clients-management:register-account',
      'clients-management:erase-account-data',
      'clients-management:complete-billing-entity-wizard',
      'clients-management:dismiss-billing-entity-wizard',
      'clients-management:reopen-billing-entity-wizard'
    ]
  },

  'clients-management:account:members:create': {
    key: 'clients-management:account:members:create',
    resource: 'accounts',
    action: 'create-member',
    label: 'Add account members',
    category: 'Clients Management',
    operations: [
      'clients-management:add-account-user'
    ],
    implies: ['clients-management:account:read']
  },

  'clients-management:account:members:delete': {
    key: 'clients-management:account:members:delete',
    resource: 'accounts',
    action: 'delete-member',
    label: 'Remove account members',
    category: 'Clients Management',
    operations: [
      'clients-management:remove-account-user'
    ],
    implies: ['clients-management:account:read']
  },

  'clients-management:subscription:read': {
    key: 'clients-management:subscription:read',
    resource: 'subscription-plans',
    action: 'read',
    label: 'Read subscription',
    category: 'Clients Management',
    operations: [
      'clients-management:subscription-plans-readmodel'
    ]
  },

  'clients-management:subscription:create': {
    key: 'clients-management:subscription:create',
    resource: 'subscription-plans',
    action: 'create',
    label: 'Create subscription plans',
    category: 'Clients Management',
    sensitive: true,
    operations: [
      'clients-management:create-subscription-plan'
    ],
    implies: ['clients-management:subscription:read']
  },

  'clients-management:subscription:update': {
    key: 'clients-management:subscription:update',
    resource: 'subscription-plans',
    action: 'update',
    label: 'Update subscription plans',
    category: 'Clients Management',
    sensitive: true,
    operations: [
      'clients-management:update-subscription-plan',
      'clients-management:set-new-accounts-subscription-plan',
      'clients-management:set-trial-plan-duration'
    ],
    implies: ['clients-management:subscription:read']
  },

  'clients-management:subscription:delete': {
    key: 'clients-management:subscription:delete',
    resource: 'subscription-plans',
    action: 'delete',
    label: 'Delete subscription plans',
    category: 'Clients Management',
    sensitive: true,
    operations: [
      'clients-management:delete-subscription-plan'
    ],
    implies: ['clients-management:subscription:read']
  },

  'clients-management:user-profiles:read': {
    key: 'clients-management:user-profiles:read',
    resource: 'user-profiles',
    action: 'read',
    label: 'Read user profiles',
    category: 'Clients Management',
    operations: [
      'clients-management:users-profiles',
      'clients-management:users-profiles-readmodel',
      'clients-management:user-preferences',
      'clients-management:user-preferences-readmodel'
    ]
  },

  'clients-management:user-profiles:create': {
    key: 'clients-management:user-profiles:create',
    resource: 'user-profiles',
    action: 'create',
    label: 'Create user profiles',
    category: 'Clients Management',
    operations: [
      'clients-management:register-user-profile',
      'clients-management:create-user-preferences',
      'clients-management:register-user-for-business-account'
    ],
    implies: ['clients-management:user-profiles:read']
  },

  'clients-management:user-profiles:update': {
    key: 'clients-management:user-profiles:update',
    resource: 'user-profiles',
    action: 'update',
    label: 'Update user profiles',
    category: 'Clients Management',
    operations: [
      'clients-management:update-user-profile',
      'clients-management:change-user-language',
      // The Dashboard-report pointer (2026-08-17). Same aggregate and same shape as
      // 'change-user-language' — a person changing their OWN settings — so it takes
      // the same capability rather than minting one for a single preference field.
      'clients-management:change-dashboard-report'
    ],
    implies: ['clients-management:user-profiles:read']
  },

  // Narrower on purpose than `user-profiles:update`. That capability is a PERSON
  // editing their own profile and is tenant-grantable; this one is the PLATFORM
  // offering names a social provider supplied and the user never did, and its one
  // command refuses to overwrite anything already set. Homing the backfill in
  // `user-profiles:update` would have meant granting the system principal the
  // ability to rewrite any profile field at will — a far wider grant than the
  // behaviour needs. Minting this key is the reviewed widening the
  // system-principal guard asks for, scoped to exactly one operation.
  'clients-management:user-profiles:system-backfill': {
    key: 'clients-management:user-profiles:system-backfill',
    resource: 'user-profiles',
    action: 'system-backfill',
    label: 'Backfill user profile names from a social provider',
    category: 'System',
    systemOwned: true,
    operations: ['clients-management:backfill-user-profile-names'],
    implies: ['clients-management:user-profiles:read']
  },

  'clients-management:user-profiles:delete': {
    key: 'clients-management:user-profiles:delete',
    resource: 'user-profiles',
    action: 'delete',
    label: 'Delete user profiles',
    category: 'Clients Management',
    operations: [
      'clients-management:remove-user-profile',
      'clients-management:erase-user-data'
    ],
    implies: ['clients-management:user-profiles:read']
  },

  // TV1-1865: domain-owned assignment groups (admin surface; drives the
  // `assigned` scope via user↔group membership).
  'clients-management:assignment-groups:read': {
    key: 'clients-management:assignment-groups:read',
    resource: 'assignment-groups',
    action: 'read',
    label: 'Read assignment groups',
    category: 'Clients Management',
    operations: [
      'clients-management:assignment-groups-readmodel'
    ]
  },

  'clients-management:assignment-groups:create': {
    key: 'clients-management:assignment-groups:create',
    resource: 'assignment-groups',
    action: 'create',
    label: 'Create assignment groups',
    category: 'Clients Management',
    operations: [
      'clients-management:create-assignment-group'
    ],
    implies: ['clients-management:assignment-groups:read']
  },

  'clients-management:assignment-groups:update': {
    key: 'clients-management:assignment-groups:update',
    resource: 'assignment-groups',
    action: 'update',
    label: 'Rename assignment groups and manage membership',
    category: 'Clients Management',
    operations: [
      'clients-management:rename-assignment-group',
      'clients-management:add-group-member',
      'clients-management:remove-group-member'
    ],
    implies: ['clients-management:assignment-groups:read']
  },

  'clients-management:assignment-groups:delete': {
    key: 'clients-management:assignment-groups:delete',
    resource: 'assignment-groups',
    action: 'delete',
    label: 'Delete assignment groups',
    category: 'Clients Management',
    operations: [
      'clients-management:delete-assignment-group'
    ],
    implies: ['clients-management:assignment-groups:read']
  },

  //*******************************************
  // Accounting (user-assignable)
  //*******************************************

  'accounting:read': {
    key: 'accounting:read',
    resource: 'chart-of-accounts',
    action: 'read',
    label: 'Read accounting',
    category: 'Accounting',
    operations: [
      'accounting:accounts',
      'accounting:account-list-readmodel',
      'accounting:fiscal-period-readmodel',
      'accounting:journal-entry-list-readmodel',
      'accounting:ledger-by-account-readmodel',
      'accounting:trial-balance-readmodel'
    ]
  },

  'accounting:accounts:create': {
    key: 'accounting:accounts:create',
    resource: 'chart-of-accounts',
    action: 'create-accounts',
    label: 'Create chart of accounts',
    category: 'Accounting',
    operations: [
      'accounting:create-chart-of-accounts',
      'accounting:add-account'
    ],
    implies: ['accounting:read']
  },

  'accounting:accounts:update': {
    key: 'accounting:accounts:update',
    resource: 'chart-of-accounts',
    action: 'update-accounts',
    label: 'Update chart of accounts',
    category: 'Accounting',
    operations: [
      'accounting:lock-account',
      'accounting:rename-account',
      'accounting:reclassify-account'
    ],
    implies: ['accounting:read']
  },

  'accounting:concepts:manage': {
    key: 'accounting:concepts:manage',
    resource: 'accounting-concepts',
    action: 'manage',
    label: 'Manage accounting concepts',
    category: 'Accounting',
    operations: []
  },

  'accounting:fiscal-period:open': {
    key: 'accounting:fiscal-period:open',
    resource: 'fiscal-period',
    action: 'open',
    label: 'Open fiscal period',
    category: 'Accounting',
    sensitive: true,
    operations: [
      'accounting:open-fiscal-period'
    ]
  },

  'accounting:fiscal-period:close': {
    key: 'accounting:fiscal-period:close',
    resource: 'fiscal-period',
    action: 'close',
    label: 'Close fiscal period',
    category: 'Accounting',
    sensitive: true,
    operations: [
      'accounting:close-fiscal-period'
    ]
  },

  'accounting:journal-entries:record': {
    key: 'accounting:journal-entries:record',
    resource: 'journal-entries',
    action: 'record',
    label: 'Record journal entries',
    category: 'Accounting',
    operations: [
      'accounting:record-journal-entry'
    ]
  },

  'accounting:journal-entries:reverse': {
    key: 'accounting:journal-entries:reverse',
    resource: 'journal-entries',
    action: 'reverse',
    label: 'Reverse journal entries',
    category: 'Accounting',
    sensitive: true,
    operations: [
      'accounting:reverse-journal-entry'
    ]
  },

  //*******************************************
  // Billing — subscription products (user-assignable)
  //*******************************************

  'billing:products:read': {
    key: 'billing:products:read',
    resource: 'billing-products',
    action: 'read',
    label: 'Read billing products',
    category: 'Billing',
    operations: [
      'billing:products',
      'billing:products-readmodel'
    ]
  },

  'billing:products:create': {
    key: 'billing:products:create',
    resource: 'billing-products',
    action: 'create',
    label: 'Create billing products',
    category: 'Billing',
    operations: [
      'billing:create-product',
      'billing:create-product-bundle'
    ],
    implies: ['billing:products:read']
  },

  'billing:products:update': {
    key: 'billing:products:update',
    resource: 'billing-products',
    action: 'update',
    label: 'Update billing products',
    category: 'Billing',
    operations: [
      'billing:archive-product',
      'billing:unarchive-product',
      'billing:update-product-name',
      'billing:update-product-charge-templates'
    ],
    implies: ['billing:products:read']
  },

  'billing:products:delete': {
    key: 'billing:products:delete',
    resource: 'billing-products',
    action: 'delete',
    label: 'Delete billing products',
    category: 'Billing',
    operations: [
      'billing:delete-product'
    ],
    implies: ['billing:products:read']
  },

  'billing:products:publish': {
    key: 'billing:products:publish',
    resource: 'billing-products',
    action: 'publish',
    label: 'Publish billing products',
    category: 'Billing',
    sensitive: true,
    operations: [
      'billing:publish-product',
      'billing:publish-rate-plan-version'
    ]
  },

  //*******************************************
  // Billing — the TENANT's own subscription (TV1-2071, user-assignable)
  //*******************************************
  // Every capability above is ADMIN power over the product catalogue. This one is
  // the opposite: the tenant acting on ITS OWN subscription. It is a separate
  // capability (not an action on `billing-products`) precisely so that granting a
  // tenant role "may pay our bill" cannot leak catalogue power, and so an operator
  // can see the two as distinct grants. Reached through `billing:*` for
  // account-owner / manager; assignable on its own to a custom role.

  'billing:my-subscription:pay': {
    key: 'billing:my-subscription:pay',
    resource: 'my-subscription',
    action: 'pay',
    label: 'Pay my subscription',
    category: 'Billing',
    // Moves real money on the tenant's own card. Not a catastrophe if
    // over-granted (the payer can only pay their OWN outstanding balance, and
    // never chooses an amount), but it is a money-moving write, so it is flagged
    // for operator review like the other money-adjacent capabilities.
    sensitive: true,
    operations: [
      // ADR-057: the slug of `PayMySubscription`, the endpoint `name` in
      // `billing/paySubscription.endpoints.ts`. Both the route-auth generator and
      // pv3's client-library generator derive `<subdomain>:<paramCase(name)>`.
      'billing:pay-my-subscription'
    ]
  },

  'billing:my-subscription:read': {
    key: 'billing:my-subscription:read',
    resource: 'my-subscription',
    action: 'read',
    label: 'View my subscription billing',
    category: 'Billing',
    // A DISTINCT action from `pay`, deliberately. Reading what you owe must not
    // imply the power to move money off the card (nor, in the other direction,
    // any power over the admin product catalogue that every `billing:products:*`
    // capability above grants). A bookkeeper role can be given this and nothing
    // else.
    //
    // Deliberately NOT flagged `sensitive` (unlike `:pay`): it exposes the
    // caller's OWN amount due, invoice history and the last four digits of their
    // own card. No credential material — the gateway token is never served (see
    // `toTenantPaymentMethod`) — and no other tenant's data is reachable, because
    // the endpoints take no account parameter.
    //
    // No `implies` is needed to reach it from a role: `matchCapabilities` expands
    // `billing:*` by KEY PREFIX, so account-owner / manager pick this up along with
    // `:pay` automatically.
    operations: [
      // ADR-057: for a `private` GET on a NON-readmodel route, `deriveOperation`
      // returns the LAST non-`:param` path segment, so these are the tail segments
      // of the three routes in `billing/mySubscription.endpoints.ts`. Renaming a
      // segment orphans the capability; `checkOperationKeyConvergence` fails the
      // build on it.
      'billing:amount-due',
      'billing:subscription-invoices',
      'billing:card-on-file',
      // TV1-2242 — `GET /billing/my-subscription/pay-attempt/:intentId`. Same
      // derivation rule: `deriveOperation` takes the last non-`:param` segment,
      // so `:intentId` is skipped and the key is the `pay-attempt` segment.
      //
      // It belongs to `:read`, not to `:pay`. Asking how your own last attempt
      // went is looking at a bill, not moving money — and the whole point of the
      // endpoint is that a tenant whose charge was DECLINED can find that out.
      // Putting it behind `:pay` (which IS flagged `sensitive`) would let a
      // read-only bookkeeper role see the amount due but not why the card failed.
      'billing:pay-attempt'
    ]
  },

  'billing:my-subscription:manage-card': {
    key: 'billing:my-subscription:manage-card',
    resource: 'my-subscription',
    action: 'manage-card',
    label: 'Manage my subscription card on file',
    category: 'Billing',
    // SENSITIVE, and for a sharper reason than `:pay`: this capability decides
    // WHICH card a collection charges. `collectResidual` charges
    // `profile.defaultPaymentMethodId`, and `AddMySubscriptionCard` always makes
    // the card it adds the default — so holding this is the power to redirect the
    // account's future charges onto a card of your choosing. It is deliberately a
    // THIRD capability rather than an operation on `:pay` or `:read`: a bookkeeper
    // who may look at the bill should not be able to swap the card, and a role
    // that may pay an existing bill is a different grant from one that may change
    // the instrument.
    sensitive: true,
    operations: [
      // ADR-057: the slug of `AddMySubscriptionCard`, the endpoint `name` in
      // `billing/mySubscription.endpoints.ts`. A private MUTATION derives its key
      // from the NAME (`paramCase`), not from the path — which is why this POST can
      // share `/billing/my-subscription/card-on-file` with the GET whose key is the
      // path-derived `billing:card-on-file` without colliding.
      'billing:add-my-subscription-card'
    ]
  },

  //*******************************************
  // Invoicing (user-assignable)
  //*******************************************

  'invoicing:billing-entities:read': {
    key: 'invoicing:billing-entities:read',
    resource: 'billing-entities',
    action: 'read',
    label: 'Read billing entities',
    category: 'Invoicing',
    operations: [
      'invoicing:billing-entities',
      'invoicing:billing-entities-readmodel',
      // TV1-2210 (S6) — `GET /billing-entity-points-of-sale`, the fiscal
      // authority's registered point-of-sale list, which the billing-entity form
      // must offer because a TYPED point of sale fails only in production (ARCA
      // 11002). A GET route derives its operation from the last non-param path
      // segment (`deriveOperation`, `scripts/generateRouteAuthConfig.ts`; only
      // private MUTATIONS use `paramCase( name )`), so the id is
      // `invoicing:billing-entity-points-of-sale`.
      //
      // It sits on `:read` rather than on `:create` / `:update` because BOTH of
      // those already `implies` it, so one entry serves every writer, and a
      // second capability for one list would be a grant nobody could reason
      // about. NOTED, not solved here: unlike its two neighbours this operation
      // makes an OUTBOUND call to ARCA, so `:read` now transitively authorizes
      // egress to the fiscal authority under the caller's own integration
      // credentials. It reads a registry and writes nothing.
      'invoicing:billing-entity-points-of-sale',
      // TV1-2206 — `GET /invoicing/taxpayer-registry`, the fiscal authority's
      // taxpayer registry (ARCA padron), which a customer form reads so a CUIT
      // pre-fills razon social + VAT condition instead of being typed. A GET
      // route derives its operation from the last non-param path segment, so the
      // id is `invoicing:taxpayer-registry`.
      //
      // It sits HERE, on `:read`, for the SAME reason as the point-of-sale list
      // directly above, and the reasoning transfers exactly: this is a
      // connector-backed OUTBOUND read performed under the caller's own
      // integration credentials, named by `(connectorId, integrationUserId)`,
      // and that comment already records that `:read` transitively authorizes
      // egress to the fiscal authority. It reads a registry and writes nothing.
      //
      // ⚠️ NOTED, NOT SOLVED, and deliberately visible: a TAXPAYER-registry read
      // sitting under a BILLING-ENTITIES key is a naming stretch. It is the
      // honest fit today because the capability being exercised IS the billing
      // entity's certificate — the lookup is impossible without one. The
      // alternative, a new `invoicing:taxpayer-registry:read`, was rejected: a
      // new key must ship a non-empty intersecting scope set or `deadGrantGate`
      // fails the build (ADR-109), and the precedent above already rejected a
      // second capability for one list as "a grant nobody could reason about".
      // `invoicing:fiscal-authority:query` was rejected earlier still, by name,
      // for modelling a CHANNEL where every other resource models an OBJECT.
      //
      // 🚩 The design block that specified this ticket resolved the key to
      // `'issues-invoices'`. That is NOT an authorization capability at all — it
      // is a CONNECTOR capability (`ConnectorAdapter`, alongside `syncs-stock`),
      // it appears nowhere in this registry, and it has zero scopes, so the
      // block's "ADR-109 is satisfied trivially by reuse" was vacuous rather
      // than true. It is still used, correctly, as the connector-role assertion
      // in `getConnector( connectorId, ['issues-invoices'] )`.
      'invoicing:taxpayer-registry'
    ]
  },

  'invoicing:billing-entities:create': {
    key: 'invoicing:billing-entities:create',
    resource: 'billing-entities',
    action: 'create',
    label: 'Create billing entities',
    category: 'Invoicing',
    operations: [
      'invoicing:add-billing-entity'
    ],
    implies: ['invoicing:billing-entities:read']
  },

  // TV1-2210 (S3). The operation id is `subdomain:paramCase(CommandName)` — the
  // convention `invoicing:add-billing-entity` follows for `AddBillingEntity`,
  // verified against `route-config.json`, where the command route
  // `POST /billing-entities/:id/add-billing-entity` carries
  // `subdomain: 'invoicing'`, `operation: 'add-billing-entity'`. Hence
  // `edit-billing-entity` / `disable-billing-entity` for `EditBillingEntity` /
  // `DisableBillingEntity`.
  'invoicing:billing-entities:update': {
    key: 'invoicing:billing-entities:update',
    resource: 'billing-entities',
    action: 'update',
    label: 'Edit billing entities',
    category: 'Invoicing',
    operations: [
      'invoicing:edit-billing-entity'
    ],
    implies: ['invoicing:billing-entities:read']
  },

  // TV1-2210 (S3). A SEPARATE capability from `:update`, not an operation on it:
  // disabling releases the entity's invoicing-integration claim and stops the
  // account issuing through it, which is a materially larger power than
  // correcting an address. See the design's R17 for the ACCEPTED RISK that this
  // is account-level rather than per-entity scoped.
  'invoicing:billing-entities:disable': {
    key: 'invoicing:billing-entities:disable',
    resource: 'billing-entities',
    action: 'disable',
    label: 'Disable billing entities',
    category: 'Invoicing',
    operations: [
      'invoicing:disable-billing-entity'
    ],
    implies: ['invoicing:billing-entities:read']
  },

  // TV1-2305 D4. WHY THIS KEY EXISTS AT ALL: `GET /billing-entity-points-of-sale`
  // sits on `:read` above, next to ordinary readmodel queries — but unlike every
  // one of its neighbours it makes an OUTBOUND call to ARCA under the ACCOUNT'S
  // OWN fiscal integration credentials. A pure read grant was therefore
  // transitively authorizing egress under the tenant's fiscal identity. This key
  // splits that boundary out so it can be granted, audited and refused on its own.
  //
  // ⚠️ `operations: []` here means something DIFFERENT from the zero-operation
  // facets elsewhere in this file, and the two are indistinguishable by shape.
  // `shipping:labels:retrieve` is a FACET: its enforcement is real and lives
  // outside the operation tier (the integrations dispatcher). This one is a
  // DECLARED BOUNDARY AWAITING ITS ENDPOINT: the refresh action does not exist
  // yet — the endpoint, the `FEParamGetPtosVenta` call, the cache and the rate
  // limit are all TV1-2304 §B. When that lands, its operation id belongs HERE and
  // must be REMOVED from `invoicing:billing-entities:read` above.
  //
  // REJECTED NAMES, with reasons, so the next reader does not relitigate them:
  //  · `invoicing:billing-entities:refresh-points-of-sale` — verbose, and it needs
  //    a sibling key on the very next fiscal-authority lookup.
  //  · `invoicing:fiscal-authority:query` — models the CHANNEL, while every other
  //    resource in this file models an OBJECT. One inconsistent resource is worse
  //    than a slightly less expressive name.
  //  · folding it into `:update` — that makes "may edit the billing entity" and
  //    "may talk to the tax authority" the same grant, which is precisely the
  //    conflation splitting it out exists to undo.
  //
  // The name GENERALIZES on purpose: the thing refreshed is the BILLING ENTITY,
  // and ARCA is merely how. Future adapter-backed refreshes of the same entity
  // (`verifyPointOfSaleRegistered`, a `getLastVoucher` reconcile) reuse this key
  // instead of minting one each time.
  'invoicing:billing-entities:refresh': {
    key: 'invoicing:billing-entities:refresh',
    resource: 'billing-entities',
    action: 'refresh',
    label: 'Refresh billing entity data from the fiscal authority (points of sale)',
    category: 'Invoicing',
    operations: [],
    implies: ['invoicing:billing-entities:read']
  },

  // TV1-2258 / D1-D2 / D9. Originally a facet with ZERO operations, enforced by
  // `OrdersFacets['invoicing:fiscal-documents:read']` at the orders readmodel's
  // `responseFormatter`, which redacts `invoiceFileUrl`. The invoice is Invoicing's
  // artifact (`ddd-patterns.md` forbids choosing a subdomain for authz convenience),
  // even though the field lives on the orders readmodel document.
  //
  // TV1-2201 — the facet now also gates a REAL read surface: `FiscalDocumentsReadmodel`
  // (`/fiscal-documents/:id?`), the first way an issued comprobante is queryable at all. Same
  // resource, same action, same audience, so it belongs on this capability rather than
  // on a new key. Mirrors `invoicing:billing-entities:read`, which likewise carries both
  // the query operation and its readmodel-subscription operation.
  //
  // TV1-2305 — the label was widened to match: `Read invoices (link, PDF)` was accurate
  // for the zero-operation facet, but roles are assigned by READING LABELS, not operation
  // arrays, and the old wording read as "PDF link only" while the key now also gates the
  // invoice's own status and number. The access DELTA is only that status/number — the
  // pre-existing facet already granted the PDF, which contains everything.
  'invoicing:fiscal-documents:read': {
    key: 'invoicing:fiscal-documents:read',
    resource: 'invoices',
    action: 'read',
    label: 'Read invoices (status, number, link, PDF)',
    category: 'Invoicing',
    operations: [
      'invoicing:fiscal-documents',
      'invoicing:fiscal-documents-readmodel'
    ]
  },

  'invoicing:fiscal-documents:generate': {
    key: 'invoicing:fiscal-documents:generate',
    resource: 'invoices',
    action: 'generate',
    label: 'Generate billing invoices (ARCA)',
    category: 'Invoicing',
    sensitive: true,
    operations: [
      'invoicing:generate-invoice',
      'invoicing:generate-invoice-from-order',
      // ADR-049 / S6 (TV1-1921) — credit notes (a new invoiceType on the same
      // invoice aggregate; the refund/concession money side of a reversal).
      'invoicing:issue-credit-note',
      'invoicing:issue-credit-note-from-reversal',
      // TV1-2207 (S4) — a FISCAL NOTE (nota de crédito / débito) is a REAL,
      // CAE-bearing, irreversible ARCA document issued on its own
      // `FiscalDocuments-{noteId}` stream through the SAME serial funnel as an invoice.
      // Same resource, same action, same audience, same `sensitive: true` blast
      // radius as generating one — so it belongs on this capability rather than a
      // new key. Distinct from `invoicing:issue-credit-note` above, which records
      // the MONEY side of a reversal and mints no fiscal document.
      'invoicing:issue-fiscal-note',
      // TV1-2207 (S5a) — the NOTA DE DEBITO entry point. Same resource, same
      // sensitive blast radius: it raises what the customer owes and (once
      // authorized) RAISES the credit-note ceiling.
      'invoicing:issue-debit-note',
      // TV1-2208 — VOID (anular) an authorized comprobante. It mints a
      // full-amount annulment NOTA DE CREDITO through this same serial funnel, so
      // it is the same resource, the same irreversible ARCA blast radius and the
      // same `sensitive: true` audience as every operation above it.
      //
      // ⚠️ SCOPE QUESTION, DELIBERATELY NOT SETTLED HERE (capabilities are
      // unreviewed): a void is arguably a HIGHER privilege than an issue — an
      // operator trusted to bill a customer is not automatically one trusted to
      // annul a comprobante after the fact. If that distinction is wanted it needs
      // its OWN key (`invoicing:fiscal-documents:void`), which is a product
      // decision, not a refactor. Filed on `:generate` for now because that is the
      // rule this capability already follows for `issue-debit-note`, and because
      // PV3 registers an endpoint for EVERY aggregate command — so the operation
      // must be covered somewhere or the auth-surface generator fails.
      'invoicing:void-invoice',
      // TV1-2259 (S7) — `RequestBuyerInvoicePreference`: record the `awaiting_reply`
      // pre-state and ask the buyer whether they want a fiscal invoice. Dispatched by a
      // seller's rule through the client library, exactly like
      // `invoicing:generate-invoice-from-order` above.
      //
      // ⚠️ SCOPE ODDITY, DELIBERATELY NOT SETTLED HERE (capabilities are UNREVIEWED —
      // this is a product decision, not a refactor). This operation mints NO fiscal
      // document and is not, on its own, worth this key's `sensitive: true` blast radius;
      // read literally, it is closer to a MESSAGING capability, since its whole outward
      // effect is a message to a buyer. It is filed here because it is the ENTRY POINT of
      // this lane — the `issueDefault` fallback it records is what a later slice will use
      // to issue a real ARCA document — and because pv3 registers a route for EVERY
      // aggregate command, so the operation must be covered somewhere or the auth-surface
      // generator fails. Same reasoning, verbatim, as `invoicing:void-invoice` above.
      'invoicing:request-buyer-invoice-preference',
      // TV1-2259 (S10) — `RecordBuyerInvoiceIntent`: record what the buyer answered about
      // wanting a fiscal invoice. Unlike the S7 entry above, filing this here is NOT an
      // oddity — this operation IS the fiscal decision. `buyerInvoiceIntent` on the
      // aggregate is the sole authority for whether an order invoice takes the BUYER's tax
      // regime (S10 deleted the provisional command field S2 added), so anything that can
      // reach this route can move a comprobante from class B to class A. Same
      // `sensitive: true` blast radius as `invoicing:generate-invoice-from-order` itself.
      //
      // ⚠️ It is ALSO in `INTERNAL_ONLY_COMMAND_ROUTES` — this capability governs the
      // in-process rules dispatch, not a gateway surface.
      'invoicing:record-buyer-invoice-intent'
    ]
  },

  //*******************************************
  // Items Publishing (Catalog) — user-assignable
  //*******************************************

  'items-publishing:items:read': {
    key: 'items-publishing:items:read',
    resource: 'items',
    action: 'read',
    label: 'Read items',
    category: 'Catalog',
    operations: [
      'items-publishing:inventory',
      'items-publishing:inventory-readmodel',
      'items-publishing:inventory-search',
      'items-publishing:inventory-distinct-categories',
      'items-publishing:inventory-spreadsheet',
      'items-publishing:inventory-spreadsheet-readmodel',
      'items-publishing:inventory-stock-break',
      'items-publishing:order-items-search',
      'items-publishing:productos',
      'items-publishing:productos-parcial',
      'items-publishing:search',
      'items-publishing:user-preferences-readmodel',
      'items-publishing:items-publishing-user-preferences-readmodel',
      'items-publishing:generate-sku',
      'items-publishing:get-sku-template'
    ]
  },

  'items-publishing:items:create': {
    key: 'items-publishing:items:create',
    resource: 'items',
    action: 'create',
    label: 'Create items',
    category: 'Catalog',
    operations: [
      'items-publishing:register-item',
      'items-publishing:create-item-with-stock',
      'items-publishing:create-items-publishing-user-preferences',
      'items-publishing:items-publishing-user-preferences-creation'
    ],
    implies: ['items-publishing:items:read']
  },

  'items-publishing:items:update': {
    key: 'items-publishing:items:update',
    resource: 'items',
    action: 'update',
    label: 'Update items',
    category: 'Catalog',
    operations: [
      'items-publishing:edit-item',
      'items-publishing:merge-items',
      'items-publishing:change-conflicted-sku',
      'items-publishing:set-category-sku-formula-override',
      'items-publishing:remove-category-sku-formula-override',
      'items-publishing:change-inventory-list-preferences',
      'items-publishing:upsert-inventory-bulk-edit-views',
      'items-publishing:upsert-inventory-price-lists-views',
      'items-publishing:upsert-inventory-stock-lists-views',
      'items-publishing:validate-publication',
      // Kit Inference (slice 1): define/re-define/clear a Composite Item's Bill of
      // Materials (incl. one-click de-kit).
      'items-publishing:set-item-composition',
      // Merge repair. Both are dispatched IN-PROCESS from the item-merge endpoint's
      // transaction and are not called directly by a client — but PV3 registers an
      // endpoint for every aggregate command, so each still needs a capability entry
      // or the auth-surface generator fails. They ride on `items:update` because they
      // are only ever reachable through a merge, which already requires it.
      'items-publishing:substitute-bom-components',
      'items-publishing:release-composite-components'
    ],
    implies: ['items-publishing:items:read']
  },

  'items-publishing:items:delete': {
    key: 'items-publishing:items:delete',
    resource: 'items',
    action: 'delete',
    label: 'Delete items',
    category: 'Catalog',
    operations: [
      'items-publishing:delete-item',
      // TV1-2120 (D-A1-3): the delete preflight — "which of these items cannot be
      // deleted, and what blocks it". A pure read, but it discloses which live listings a
      // delete would close, so it is gated with the delete itself rather than left on the
      // generic item read.
      'items-publishing:item-delete-preflight',
      'items-publishing:destroy-items-publishing-user-preferences'
    ],
    implies: ['items-publishing:items:read']
  },

  'items-publishing:items:bulk': {
    key: 'items-publishing:items:bulk',
    resource: 'items',
    action: 'bulk',
    label: 'Bulk edit items',
    category: 'Catalog',
    sensitive: true,
    operations: [
      'items-publishing:apply-bulk-items-change',
      // Also the inbound ERP price-sync terminal since TV1-2108 / ADR-190 D16 retired
      // the separate `items-publishing:set-price-list-item` operation.
      'items-publishing:apply-bulk-price-change'
    ]
  },

  'items-publishing:publications:read': {
    key: 'items-publishing:publications:read',
    resource: 'publications',
    action: 'read',
    label: 'Read publications',
    category: 'Catalog',
    operations: [
      'items-publishing:publications',
      'items-publishing:publications-readmodel',
      'items-publishing:publications-search',
      'items-publishing:publications-count',
      'items-publishing:publications-distinct-categories',
      'items-publishing:publicationsIds',
      'items-publishing:publications-retail-codes',
      'items-publishing:publications-retail-code-readmodel',
      'items-publishing:fenicio-publications-readmodel',
      'items-publishing:duplicate-diff',
      'items-publishing:channels-publication-form-type',
      'items-publishing:channels-publication-default-values-type',
      'items-publishing:channels-commisions',
      // TV1-2105 (Observation Mode, D5, slice 7): the Drift Episode audit
      // time-series — a read surface over the publications' channel-drift history,
      // scope-anchored on the same `channelIntegrationId` as publications.
      'items-publishing:publication-drift-episodes',
      'items-publishing:publication-drift-episodes-search',
      'items-publishing:publication-drift-episodes-readmodel'
    ]
  },

  'items-publishing:publications:create': {
    key: 'items-publishing:publications:create',
    resource: 'publications',
    action: 'create',
    label: 'Create publications',
    category: 'Catalog',
    operations: [
      'items-publishing:add-publication',
      'items-publishing:duplicate-publication'
    ],
    implies: ['items-publishing:publications:read']
  },

  'items-publishing:publications:update': {
    key: 'items-publishing:publications:update',
    resource: 'publications',
    action: 'update',
    label: 'Update publications',
    category: 'Catalog',
    operations: [
      'items-publishing:edit-publication',
      'items-publishing:publications-batch-editing',
      'items-publishing:upsert-publications-bulk-edit-views',
      // TV1-1968: pre-existing drift fix. The bulk-edit batch-process trigger is
      // the batch-orchestration variant of the bulk-edit feature already owned
      // here (`publications-batch-editing` / `upsert-publications-bulk-edit-views`);
      // bulk edit = update (non-sensitive), so it belongs on publications:update,
      // NOT the sensitive publications:publish (which owns create/status/duplicate
      // start-batch-process ops). Was registered in route-config but no capability.
      'items-publishing:record-external-publication-error',
      // TV1-2393 (D4/D5/D11, slice 4) — the Observed Operation record `createPublication`
      // / `republishPublication` write when the account's channel egress is suppressed.
      // Owned here, beside `record-external-publication-error`, on the same reasoning:
      // an internal, non-sensitive write onto the publication's OWN state, never
      // user-facing — every command route still needs a capability entry.
      'items-publishing:record-publication-operation-observation',
      // TV1-2105 (Observation Mode, ADR-069): the stock/price rules write the
      // rule-computed Desired Channel State onto the publication's OWN channel-facing
      // fields (`variations[].stock` / price) via this command, which emits
      // `PublicationEdited`. An internal rule-driven update to the publication's own
      // state (mirroring `record-external-publication-error`) — not user-facing, but
      // every command route needs a capability entry. The paired internal
      // `publication-desired-channel-state-push` policy route is the SINGLE origin of
      // every per-element channel egress request — stock/price + content/status (both
      // via PublicationEdited) and delist (PublicationDelisted, folded in from the
      // retired ChannelDelistPolicy).
      'items-publishing:set-publication-channel-values',
      'items-publishing:publication-desired-channel-state-push'
    ],
    implies: ['items-publishing:publications:read']
  },

  'items-publishing:publications:import': {
    key: 'items-publishing:publications:import',
    resource: 'publications',
    action: 'import',
    label: 'Import publications',
    category: 'Catalog',
    sensitive: true,
    operations: [
      'items-publishing:import-publication',
      'items-publishing:import-publications',
      'items-publishing:sync-publications-from-channel'
    ]
  },

  // NOTE (ERP-ONBOARDING O9): `items-publishing:publications:publish` was RETIRED here.
  // Every operation it owned was a `Start*BatchProcess` command, and those moved with the
  // batch-process aggregate to the neutral `batch-processes` subdomain. They are now owned by
  // `batch-processes:processes:start` (still `sensitive`). A capability with an empty
  // `operations` array grants nothing, so leaving it behind would have been a lie in the
  // permissions UI. Any custom role that held it must be re-granted `batch-processes:*` (or
  // `batch-processes:processes:start`) — system roles inherit it via ALL_SUBDOMAIN_WILDCARDS.

  'items-publishing:publications:delete': {
    key: 'items-publishing:publications:delete',
    resource: 'publications',
    action: 'delete',
    label: 'Delete publications',
    category: 'Catalog',
    sensitive: true,
    operations: [
      // TV1-1594 (ADR-060): the user-facing "delete item(s)" command delists the
      // publication (`delete-publication`). TV1-2105 (D4) retired the standalone
      // `ChannelDelistPolicy` — its external-delist reaction was folded into the
      // single-origin `PublicationDesiredChannelStatePushPolicy` (whose internal
      // `publication-desired-channel-state-push` route lives under `publications:publish`),
      // so there is no longer a separate `channel-delist` route/operation. Sensitive:
      // it removes a live external listing.
      'items-publishing:delete-publication',
      // TV1-2120 (D-A1-1, ADR-060 Amendment 1): relist is the REVERSE of delist and
      // deliberately shares its capability. The pair is one reversible lifecycle
      // control over a live external listing, so granting the power to close a listing
      // and the power to reopen the one you closed is a single decision; splitting them
      // would let a role delist with no way to undo its own mistake — which is most of
      // why the delete button is frightening in the first place (A1 / D-A1-1).
      'items-publishing:relist-publication'
    ],
    implies: ['items-publishing:publications:read']
  },

  'items-publishing:price-lists:read': {
    key: 'items-publishing:price-lists:read',
    resource: 'price-lists',
    action: 'read',
    label: 'Read price lists',
    category: 'Catalog',
    operations: [
      'items-publishing:price-lists',
      'items-publishing:price-list-metadata-readmodel'
    ]
  },

  'items-publishing:price-lists:update': {
    key: 'items-publishing:price-lists:update',
    resource: 'price-lists',
    action: 'update',
    label: 'Create or update price lists',
    category: 'Catalog',
    operations: [
      'items-publishing:upsert-price-list'
    ],
    implies: ['items-publishing:price-lists:read']
  },

  'items-publishing:price-lists:delete': {
    key: 'items-publishing:price-lists:delete',
    resource: 'price-lists',
    action: 'delete',
    label: 'Delete price lists',
    category: 'Catalog',
    operations: [
      'items-publishing:delete-price-list'
    ],
    implies: ['items-publishing:price-lists:read']
  },

  // TV1-116: gates all user-driven size-guide work — create / update / delete,
  // channel binding, import. The SizeGuideAggregate (UpsertSizeGuide /
  // DeleteSizeGuide) and SizeGuideReadmodel (base `/items-publishing/size-guides`
  // + `-search` query routes, plus the auto-generated internal
  // `size-guide-readmodel` collection-query endpoint) ship with this ticket.
  // Operation keys mirror the price-lists convention: command-name → kebab,
  // readmodel-route last-segment → kebab, readmodel-name → `<name>-readmodel`.
  'items-publishing:size-guides:manage': {
    key: 'items-publishing:size-guides:manage',
    resource: 'size-guides',
    action: 'manage',
    label: 'Manage size guides',
    category: 'Catalog',
    operations: [
      'items-publishing:upsert-size-guide',
      'items-publishing:delete-size-guide',
      'items-publishing:size-guides',
      'items-publishing:size-guides-search',
      'items-publishing:size-guide-readmodel',
      'items-publishing:size-guides-channel-charts',
      'items-publishing:size-guides-active-domains',
      'items-publishing:size-guides-publish-to-channel'
    ]
  },

  // ERP-ONBOARDING O9: the batch-process SUBSTRATE moved to its own neutral
  // `batch-processes` subdomain (see the block near the end of this file). What is
  // left here is the publication-SPECIFIC bulk-edit RESULTS readmodel, which knows
  // what a publicationId is and therefore stayed behind.
  // The key's MIDDLE segment is what the role editor renders as a sub-header
  // (`catalog.ts` groups a category's capabilities by `key.split(':')[1]`), so it has
  // to name what the capability actually grants — bulk-edit results. Leaving it as
  // `batch-processes` would print a "Batch processes" sub-group holding no
  // batch-process capability, right beside the `batch-processes:processes:*` group
  // that holds them all. `key` and `resource` are independent fields and routinely
  // diverge (most three-segment keys in this registry do, e.g. `identity:users:read`
  // → `resource: 'auth-users'`), so the rename costs nothing: `resource` stays
  // `batch-processes` because that is the scope-intersection resource
  // (scopes `['own','all']`) these rows authorize against — they ARE a batch
  // process's per-target results.
  // Note this is NOT the neutral subdomain's `batch-processes:processes:read` — that
  // one serves the batch list itself; this one only serves the publication-specific
  // results grid, which is why it stayed under `items-publishing`.
  // A custom role still holding the retired `items-publishing:batch-processes:read`
  // fails LOUDLY (`unknown capability key`, validateGrants.ts) on its next update —
  // preferable to the silent narrowing that keeping the key would have produced.
  'items-publishing:bulk-edit-results:read': {
    key: 'items-publishing:bulk-edit-results:read',
    resource: 'batch-processes',
    action: 'read',
    label: 'Read publication bulk-edit results',
    category: 'Catalog',
    operations: [
      'items-publishing:publications-bulk-edit-results',
      'items-publishing:publications-bulk-edit-results-readmodel'
    ]
  },

  //*******************************************
  // Batch Processes (neutral subdomain — ERP-ONBOARDING O9)
  //*******************************************
  // The batch-process substrate moved out of items-publishing into its own subdomain, and
  // authorization moved WITH it: the gateway matches a grant's leading segment against the
  // route's `x-subdomain`, so an `items-publishing:*` grant no longer reaches these routes.
  // `batch-processes:*` is registered in `ALL_SUBDOMAIN_WILDCARDS`
  // (identity/teselly-better-auth/authorization/roles-and-permissions.ts) so account-owner and
  // manager keep their access, and operator gets an explicit `['own']` grant mirroring its old
  // `items-publishing:*` scope. Custom roles holding the retired fine-grained items-publishing
  // keys must be re-granted — that failure mode is an EMPTY navbar dropdown, not a build error.

  'batch-processes:processes:read': {
    key: 'batch-processes:processes:read',
    resource: 'batch-processes',
    action: 'read',
    label: 'Read batch processes',
    category: 'Catalog',
    operations: [
      'batch-processes:batch-processes',
      'batch-processes:batch-processes-readmodel',
      'batch-processes:progress',
      'batch-processes:batch-progress-readmodel',
      'batch-processes:progress-count'
    ]
  },

  'batch-processes:processes:start': {
    key: 'batch-processes:processes:start',
    resource: 'batch-processes',
    action: 'start',
    label: 'Start batch processes',
    category: 'Catalog',
    sensitive: true,
    operations: [
      'batch-processes:start-publications-duplication-batch-process',
      'batch-processes:start-publication-status-changed-batch-process',
      'batch-processes:start-publication-bulk-edit-batch-process',
      'batch-processes:start-channel-publication-import-batch-process',
      // Channel Order Seeding. USER-facing (unlike the three ERP sweep starts under
      // `batch-processes:internal`): the operator ticks "import my order history" on
      // channel connect, exactly as they do for publications above, and the aggregate
      // materializes the denominator from the channel. Whoever may start a publication
      // import may start an order seeding — same trigger, same screen, same blast
      // radius (the seeded orders are inert records; see `channelOrderImport.policy.ts`).
      // Inert BECAUSE this start accepts no `since` (TV1-2446): the windowed, LIVE-order
      // import is `start-credential-restoration-order-import-batch-process`, internal, below.
      'batch-processes:start-channel-order-import-batch-process',
      'batch-processes:start-publication-creation-batch-process',
      'batch-processes:start-publication-sync-from-channel-batch-process'
    ],
    implies: ['batch-processes:processes:read']
  },

  'batch-processes:processes:update': {
    key: 'batch-processes:processes:update',
    resource: 'batch-processes',
    action: 'update',
    label: 'Manage batch processes (archive, cancel, retry)',
    category: 'Catalog',
    operations: [
      'batch-processes:archive-batch-process',
      'batch-processes:unarchive-batch-process',
      'batch-processes:cancel-batch-process',
      'batch-processes:retry-batch-process'
    ],
    implies: ['batch-processes:processes:read']
  },

  // System-driven callbacks: the completion command a process-manager policy dispatches, the
  // retry / cleanup policies, and the item-extraction start (never a direct user action — it is
  // reached through the extraction flow). All were `internal` before the move; they stay internal.
  'batch-processes:internal': {
    key: 'batch-processes:internal',
    resource: 'batch-processes',
    action: 'internal',
    label: 'Batch process callbacks',
    category: 'System',
    systemOwned: true,
    operations: [
      'batch-processes:complete-batch-process',
      // The terminal-FAILURE counterpart of `complete-batch-process`, and identical in
      // shape: dispatched in-process by a batch-running policy just before it rethrows,
      // never by a browser. Without it a run that failed wholesale reached no terminal
      // state at all — the row stayed 'running' at zero progress forever.
      'batch-processes:fail-batch-process',
      'batch-processes:start-publication-item-extraction',
      // ERP-ONBOARDING slice 5. Never a direct user action: the operator triggers
      // `systems-integrations:request-erp-bulk-import` from the ERP Items tab, and the
      // ERP module's own policy resolves the catalog denominator before dispatching
      // this start command in-process. Same shape as `start-publication-item-extraction`
      // above, which is likewise reached only through its own flow.
      'batch-processes:start-erp-item-import-batch-process',
      // ERP-ONBOARDING slice 9 — the STOCK sweep's start, and likewise never a direct
      // user action: the operator confirms on `systems-integrations:erp-stock-import-stock-import`,
      // and that endpoint re-enumerates the ERP's containers, refuses when none is
      // mapped, resolves the (products x mapped warehouses) denominator and only then
      // dispatches this start command in-process.
      'batch-processes:start-erp-stock-import-batch-process',
      // ERP-ONBOARDING slice 10 — the PRICE sweep's start, and likewise never a direct
      // user action: the operator confirms on `systems-integrations:erp-price-import-price-import`,
      // and that endpoint re-enumerates the ERP's price lists, refuses when none is
      // mapped, counts the priced rows across the mapped ones and only then dispatches
      // this start command in-process.
      'batch-processes:start-erp-price-import-batch-process',
      // TV1-2446 — the Credential Restoration order catch-up's start, the ONLY start that
      // carries a `since` (and so imports orders LIVE rather than as inert history).
      // Dispatched in-process by `CredentialRestorationChannelCatchUpPolicy`, never by a
      // browser; internal-only, so the user-facing seeding start cannot become it.
      'batch-processes:start-credential-restoration-order-import-batch-process',
      'batch-processes:batch-process-retry',
      'batch-processes:batch-progress-cleanup'
    ]
  },

  'items-publishing:configuration:create': {
    key: 'items-publishing:configuration:create',
    resource: 'publication-config',
    action: 'create',
    label: 'Create publishing configuration',
    category: 'Catalog',
    sensitive: true,
    operations: [
      'items-publishing:create-items-publishing-account-configuration'
    ]
  },

  // NOTE: these two are read-models bundled under the former `:manage`. Per the
  // per-resource-subset rule they stay write-tier (`:update`) rather than minting
  // a new `:read`; revisit if publishing-config should expose a read capability.
  'items-publishing:configuration:update': {
    key: 'items-publishing:configuration:update',
    resource: 'publication-config',
    action: 'update',
    label: 'Read/update publishing configuration',
    category: 'Catalog',
    sensitive: true,
    operations: [
      'items-publishing:account-configuration-readmodel',
      'items-publishing:items-publishing-account-configuration-readmodel'
    ]
  },

  'items-publishing:taxonomies:manage': {
    key: 'items-publishing:taxonomies:manage',
    resource: 'taxonomies-ml',
    action: 'manage',
    label: 'Manage taxonomies',
    category: 'Catalog',
    systemOwned: true,
    operations: [
      'items-publishing:mercadolibre-categories',
      'items-publishing:mercadolibre-categories-readmodel',
      'items-publishing:mercadolibre-discover-category-by-title',
      'items-publishing:mercadolibre-search-products-catalog',
      'items-publishing:google-products-categories-readmodel',
      'items-publishing:import-mercadolibre-taxonomies',
      'items-publishing:import-google-products-taxonomies',
      'items-publishing:complete-mercadolibre-categories-import',
      'items-publishing:complete-google-products-categories-import',
      'items-publishing:upsert-multiple-mercadolibre-categories',
      'items-publishing:upsert-google-product-category',
      'items-publishing:index-google-products-categories',
      'items-publishing:index-mercadolibre-categories',
      'items-publishing:mercadolibre-taxonomy-import-processing',
      'items-publishing:google-products-taxonomy-import-processing'
    ]
  },

  //*******************************************
  // Sales (user-assignable)
  //*******************************************

  'sales:orders:read': {
    key: 'sales:orders:read',
    resource: 'orders',
    action: 'read',
    label: 'Read orders',
    category: 'Sales',
    operations: [
      'sales:orders',
      'sales:orders-readmodel',
      'sales:orders-search',
      'sales:orders-count',
      'sales:ordersIds',
      'sales:order-statuses'
    ]
  },

  // TV1-2258 / D1-D2 / D9. NOT a row-level scope and NOT a second capability on the
  // `/sales/orders*` route (that pairing is `doublyReferenced`-build-failing). This is a
  // FACET — it owns ZERO operations, exactly like `shipping:labels:retrieve` below, and its
  // enforcement lives at the readmodel's `responseFormatter` (`OrdersFacets` /
  // `redactFacets`, `teselly-domain/src/sales/orders.facets.ts`), which redacts
  // `grossTotal`/`netTotal`/`subTotal`/`discount`/`shippingCost`/`items[].price`/
  // `items[].saleFee` from the order document unless the caller holds this key. Chosen over
  // `read-totals` because the field set is wider than totals (per-line prices too).
  // ⚠️ Because it owns no operation, `generate-capability-registry`'s drift check does not
  // cover it — `checkSystemPrincipalGrants.ts`'s `faceted` map is what keeps it honest (see
  // that script's header comment).
  'sales:orders:read-financials': {
    key: 'sales:orders:read-financials',
    resource: 'orders',
    action: 'read-financials',
    label: 'Read order financials (totals, per-line prices)',
    category: 'Sales',
    operations: []
  },

  'sales:orders:place': {
    key: 'sales:orders:place',
    resource: 'orders',
    action: 'place',
    label: 'Place orders',
    category: 'Sales',
    operations: [
      'sales:place-order',
      'sales:assign-billing-entity'
    ]
  },

  'sales:orders:update-status': {
    key: 'sales:orders:update-status',
    resource: 'orders',
    action: 'update-status',
    label: 'Update order status',
    category: 'Sales',
    operations: [
      'sales:change-order-status',
      'sales:register-shipment',
      'sales:record-shipment-status',
      // TV1-1890 shipments/logistics: the order ship / print-label affordance.
      'sales:shipment-label-operation',
      // ADR-049 / S1-S2: whole/partial-order reversal commands.
      'sales:cancel-order',
      'sales:return-order'
    ]
  },


  // TV1-2205 — the Customer master (a `sales` aggregate; there is no `customers`
  // bounded context). Its resource is TENANT-GLOBAL (`all` only): a Customer is
  // NEVER shared across accounts (R3) and carries no channel/principal anchor, so
  // the account boundary IS the access model — same shape as `billing-entities`.
  'sales:customers:read': {
    key: 'sales:customers:read',
    resource: 'customers',
    action: 'read',
    label: 'Read customers',
    category: 'Sales',
    operations: [
      'sales:customers',
      'sales:customers-search',
      'sales:customers-readmodel'
    ]
  },

  'sales:customers:manage': {
    key: 'sales:customers:manage',
    resource: 'customers',
    action: 'manage',
    label: 'Manage customers',
    category: 'Sales',
    operations: ['sales:register-customer']
  },

  'sales:analytics:read': {
    key: 'sales:analytics:read',
    resource: 'orders',
    action: 'read-analytics',
    label: 'Read sales analytics',
    category: 'Sales',
    operations: [
      'sales:sales-analytics-readmodel',
      // ADR-049: reversal fact projection for the Analytics/Dashboard/Reports epic.
      'sales:reversal-fact-readmodel'
    ]
  },

  'sales:configuration:manage': {
    key: 'sales:configuration:manage',
    resource: 'sales-configuration',
    action: 'manage',
    label: 'Manage sales configuration',
    category: 'Sales',
    operations: [
      'sales:account-configuration',
      'sales:create-sales-account-configuration',
      'sales:sales-account-configuration-readmodel',
      'sales:set-order-statuses'
    ]
  },

  //*******************************************
  // Stock Management (user-assignable)
  //*******************************************

  // TV1-2031 (F6): `warehouse-config` is the account's Default Warehouse POINTER
  // query (`WarehouseAccountConfigReadmodel` — one row per account naming the ONE
  // default warehouse). It is a read-only projection of data already exposed by
  // `stock-management:warehouses`, so it belongs to the same read capability: a
  // principal who may list the account's warehouses may see which of them is the
  // default. It grants no write.
  'stock-management:warehouses:read': {
    key: 'stock-management:warehouses:read',
    resource: 'warehouses',
    action: 'read',
    label: 'Read warehouses',
    category: 'Stock',
    operations: [
      'stock-management:warehouses',
      'stock-management:warehouses-readmodel',
      // The pointer's query route, plus its internal event-delivery route (the
      // outbox POSTs projected events there) — mirrors the `warehouses` /
      // `warehouses-readmodel` pair above.
      'stock-management:warehouse-config',
      'stock-management:warehouse-account-config-readmodel'
    ]
  },

  'stock-management:warehouses:create': {
    key: 'stock-management:warehouses:create',
    resource: 'warehouses',
    action: 'create',
    label: 'Create warehouses',
    category: 'Stock',
    operations: [
      'stock-management:register-warehouse'
    ],
    implies: ['stock-management:warehouses:read']
  },

  // TV1-2031: `set-default-warehouse` moves the account's Default Warehouse
  // Pointer. It is an UPDATE on the `warehouses` resource — it reconfigures which
  // existing warehouse is the account default; it creates and deletes nothing.
  // (Registering a warehouse with `isDefault` moves the pointer too, and that
  // path is already gated by `warehouses:create`.)
  'stock-management:warehouses:update': {
    key: 'stock-management:warehouses:update',
    resource: 'warehouses',
    action: 'update',
    label: 'Update warehouses',
    category: 'Stock',
    operations: [
      'stock-management:edit-warehouse',
      'stock-management:set-default-warehouse'
    ],
    implies: ['stock-management:warehouses:read']
  },

  'stock-management:warehouses:delete': {
    key: 'stock-management:warehouses:delete',
    resource: 'warehouses',
    action: 'delete',
    label: 'Delete warehouses',
    category: 'Stock',
    operations: [
      'stock-management:remove-warehouse'
    ],
    implies: ['stock-management:warehouses:read']
  },

  // TV1-1865: assign a warehouse to PRINCIPALS — users or groups (drives the
  // `assigned` scope). Anchored on the `warehouses` resource.
  // TV1-1881 (SDD amendment §5): the ownership-transfer capability is GONE —
  // a warehouse is delegated access, not transferable ownership. The
  // many-to-many assign now owns the derived `modify-warehouse-assignment`
  // operation ( replaces the group-specific `modify-warehouse-group-assignment` ).
  'stock-management:warehouses:assign': {
    key: 'stock-management:warehouses:assign',
    resource: 'warehouses',
    action: 'assign',
    label: 'Assign warehouse to users or groups',
    category: 'Stock',
    operations: [
      'stock-management:modify-warehouse-assignment'
    ],
    implies: ['stock-management:warehouses:read']
  },

  'stock-management:stock:read': {
    key: 'stock-management:stock:read',
    resource: 'stock-availability',
    action: 'read',
    label: 'Read stock',
    category: 'Stock',
    operations: [
      'stock-management:stock-analytics-readmodel',
      // TV1-2229 / ADR-131 — the oversell worklist. A READ capability, deliberately: the
      // table is an operator worklist, and seeing what was sold short is the same class of
      // permission as seeing stock itself.
      'stock-management:shortfalls',
      'stock-management:stock-shortfalls-readmodel',
      // TV1-2256 / ADR-143 — what an order actually MOVED per (item, warehouse). Same
      // read class as the two above: it is stock data, sliced by order.
      'stock-management:movements-by-order',
      'stock-management:stock-movements-by-order-readmodel'
    ]
  },

  'stock-management:stock:update': {
    key: 'stock-management:stock:update',
    resource: 'stock-availability',
    action: 'update',
    label: 'Update stock',
    category: 'Stock',
    operations: [
      'stock-management:initialize-warehouse-item-stock',
      'stock-management:set-warehouse-item-stock-available',
      // TV1-2229 / ADR-131 — `decrease-warehouse-item-stock-available` WAS HERE. That
      // command is gone; a single-item decrement is now a one-row
      // `update-multiple-warehouse-items-stock`, which is why that operation MOVED here
      // out of the `sensitive` `stock:bulk` bag. Leaving it in bulk would have silently
      // taken single-item decrement away from every principal holding `stock:update`, and
      // restoring it would have meant granting `apply-bulk-stock-change` too — a real
      // privilege escalation. After the TV1-2229 pivot this operation is no longer an
      // administrative bulk tool anyway: it is THE ordinary sale-path decrement leg that
      // every single-item decrement traverses. `stock:bulk` keeps the two genuinely
      // administrative operations.
      //
      // It is a MOVE and not an addition because the registry inverse must be a TOTAL,
      // UNIQUE function — `scripts/generate-operation-capability.ts` throws
      // `RegistryDefectError` on any operation owned by more than one capability, so dual
      // membership is not expressible.
      'stock-management:update-multiple-warehouse-items-stock',
      'stock-management:increase-warehouse-item-stock-available',
      // TV1-2327 / ADR-144 — the BULK restore leg, dispatched by the Order Reversal Stock
      // Restoration rule. It sits beside its single-item sibling above and NOT in the
      // `sensitive` `stock:bulk` bag: it is the ordinary reversal-path restore, not an
      // administrative tool, and its ceiling is the aggregate's per-order invariant rather
      // than the capability. Single membership only — the registry inverse must stay a
      // TOTAL, UNIQUE function (see the note above).
      'stock-management:increase-multiple-warehouse-items-stock',
      'stock-management:delete-warehouse-item'
    ]
  },

  'stock-management:stock:bulk': {
    key: 'stock-management:stock:bulk',
    resource: 'stock-availability',
    action: 'bulk',
    label: 'Bulk update stock',
    category: 'Stock',
    sensitive: true,
    operations: [
      'stock-management:apply-bulk-stock-change',
      'stock-management:initialize-multiple-warehouse-items-stock'
      // `update-multiple-warehouse-items-stock` moved to `stock:update` — see TV1-2229 /
      // ADR-131 note there.
    ]
  },

  //*******************************************
  // Sales Channels Integrations (user-assignable)
  //*******************************************

  'sales-channels:read': {
    key: 'sales-channels:read',
    resource: 'sales-channels',
    action: 'read',
    label: 'Read sales channels',
    category: 'Integrations',
    operations: [
      'systems-integrations:systems-integrations-connectors',
      'systems-integrations:connectors-readmodel',
      'systems-integrations:integrations-readmodel',
      'systems-integrations:external-systems-integrations',
      // TV1-1890 shipments/logistics: read of external logistics integrations.
      // DELIBERATE grouping ( TV1-1897 review, Medium #2 — not an expedient
      // gate-pass ): `sales-channels:read` is the umbrella for EVERY
      // systems-integrations read operation ( connectors, integrations-readmodel,
      // external-systems-integrations, channel-categories, … ). A logistics
      // integration is just another systems-integration, so its read belongs in
      // the same bag — a channel operator who can list channels/connectors can
      // legitimately list logistics carriers. Breaking it out into a lone
      // `sales-channels:logistics:read` would FRAGMENT this consistent model for
      // no access-control gain. Revisit only if logistics earns its own
      // delegation story.
      'systems-integrations:external-logistics-integrations',
      'systems-integrations:systems-integrations',
      // `systems-integrations-webhooks-post` was REMOVED from this capability when the
      // inbound-webhook ingress became `visibility: 'public'`: a public route carries no
      // capability grant, so route-config no longer emits the operation and a reference
      // here is drift. The routes on that path that DO need a caller identity
      // (`get-authorization-url`, `authorize`) enforce it inside `processRequest`, not
      // through this registry.
      'systems-integrations:read-external-systems',
      'systems-integrations:channel-import-preview-fetch-by-i-ds',
      'systems-integrations:channel-import-preview-resolve-selection',
      'systems-integrations:channel-import-preview',
      'systems-integrations:channel-import-preview-prepare',
      'systems-integrations:channel-categories',
      'systems-integrations:channel-categories-search',
      // TV1-2108 / ADR-190 D17/D18: the ERP audit read surfaces — the Sync Mirror
      // ("what Odoo last told us") and the Drift Episode time-series ("where we and
      // Odoo disagreed, and for how long"). Grouped under the same umbrella as every
      // other systems-integrations read and scope-anchored on `integrationId`, so an
      // operator who can see an integration can see its divergence history. Mirrors
      // `items-publishing:publication-drift-episodes*`.
      'systems-integrations:erp-sync-mirror',
      'systems-integrations:erp-sync-mirror-search',
      'systems-integrations:erp-sync-mirror-readmodel',
      'systems-integrations:erp-drift-episodes',
      'systems-integrations:erp-drift-episodes-search',
      'systems-integrations:erp-drift-episodes-readmodel',
      // TV1-2336 D24 — the ACCOUNT EMAILS read surface: "was this supplier actually
      // emailed?". It lives under the same systems-integrations READ umbrella as every
      // other read here rather than under this unit's own
      // `sales-channels:account-email:send`, because that entry is the SEND authority
      // (systemOwned, sensitive, granted to the system principal so a rule may send mail
      // as the account). Folding a human-facing read into it would hand every reader the
      // send grant. The rows carry no assignable anchor — an account email belongs to the
      // account, not to a channel — so the account filter is the whole boundary.
      'systems-integrations:account-emails',
      'systems-integrations:account-emails-readmodel',
      // ERP-ONBOARDING slice 7 — ERP CONTAINER DISCOVERY: a synchronous, read-only
      // enumeration of the ERP's own `stock.warehouse` rows (O1/O2). It creates
      // nothing on either side, so it belongs with every other systems-integrations
      // READ rather than with the import that consumes it. The import itself
      // (`erp-container-import-warehouse-import`) is a mutation and lives under
      // `sales-channels:integrations:configure`.
      'systems-integrations:warehouse-discovery',
      // ERP-ONBOARDING slice 8 — the same read, for PRICE LISTS: a synchronous,
      // read-only enumeration of the ERP's own `product.pricelist` rows with their
      // currencies (O1/O3). Creates nothing on either side, so it sits with
      // `warehouse-discovery` under the systems-integrations READ umbrella; the import
      // it feeds (`erp-container-import-price-list-import`) is a mutation and lives under
      // `sales-channels:integrations:configure`.
      'systems-integrations:price-list-discovery',
      // ERP-ONBOARDING slice 9 — the STOCK IMPORT PROJECTION (O11): a synchronous,
      // read-only `search_count` over the ERP catalogue plus a fresh `stock.warehouse`
      // enumeration, answering "how many rows will this import write, and across how many
      // of the warehouses your ERP actually has?". It writes nothing on either side, so it
      // sits with the two discovery reads; the sweep it feeds
      // (`erp-stock-import-stock-import`) is a mutation under
      // `sales-channels:integrations:configure`.
      'systems-integrations:stock-import-projection',
      // ERP-ONBOARDING slice 10 — the PRICE IMPORT PROJECTION (O11): a synchronous,
      // read-only `search_count` per mapped Odoo pricelist plus a fresh
      // `product.pricelist` enumeration, answering "how many rows will this import write,
      // and across how many of the price lists your ERP actually has?". It writes nothing
      // on either side, so it sits with the discovery reads and the stock projection; the
      // sweep it feeds (`erp-price-import-price-import`) is a mutation under
      // `sales-channels:integrations:configure`.
      'systems-integrations:price-import-projection'
    ]
  },

  //*******************************************
  // Test Channel — the mock marketplace (TV1-2338 slice 5)
  //*******************************************
  //
  // These two capabilities exist because the Test Channel now ships in EVERY
  // environment: its seller routes are in `route-config.json`, they carry the
  // `systems-integrations` subdomain (design fork 7 — no new subdomain in the shared
  // vocabulary), and `scripts/generate-capability-registry.ts` fails the build on any
  // subdomain-bearing operation no capability owns.
  //
  // WHY THEIR OWN CAPABILITIES RATHER THAN A HOME IN `sales-channels:read` /
  // `:integrations:configure`: `/authorization/capability-registry` strips the
  // test-channel slice of the catalog for accounts without the
  // `test-channel-connector` flag (`capability-registry.endpoint.ts`, call site (c) of
  // the per-account gate). A capability that carries ONLY test-channel operations
  // disappears whole for those accounts; one that merely also carried some would be
  // offered with holes in it. Keeping the surface in its own two capabilities is what
  // makes that filter clean.
  //
  // THE KEYS ARE PART OF THE CONTRACT: the strip filter matches `:test-channel:` in the
  // capability KEY as well as `:test-channel-` in the operation ids. The key match is
  // what covers the three READ operations below — a private read's operation id is
  // PATH-derived (`by-sku`, `order-shipments-readmodel`, `storefront-link`), so it does
  // not carry the `test-channel-` prefix that the mutations get from their endpoint
  // `name`. Renaming either key without updating that filter would silently re-offer
  // this surface to every account.

  'sales-channels:test-channel:read': {
    key: 'sales-channels:test-channel:read',
    resource: 'sales-channels',
    action: 'read',
    label: 'Read the mock test marketplace',
    category: 'Integrations',
    operations: [
      // The seller-facing reads of the mock marketplace. Each is `private` +
      // `systems-integrations` in route-config; the buyer storefront's own reads are
      // `public` routes authorised by the storefront token and carry no subdomain, so
      // they are not — and must not be — listed here.
      'systems-integrations:storefront-link',
      'systems-integrations:by-sku',
      'systems-integrations:order-shipments-readmodel'
    ]
  },

  'sales-channels:test-channel:operate': {
    key: 'sales-channels:test-channel:operate',
    resource: 'sales-channels',
    action: 'operate',
    label: 'Operate the mock test marketplace',
    category: 'Integrations',
    // It drives real orders, invoices and shipments into the platform for the calling
    // account — mock only in where the data comes from, not in what it triggers.
    sensitive: true,
    operations: [
      'systems-integrations:test-channel-create-publication',
      'systems-integrations:test-channel-edit-publication',
      'systems-integrations:test-channel-remove-publication',
      'systems-integrations:test-channel-sync-publication',
      'systems-integrations:test-channel-update-publication-stock',
      'systems-integrations:test-channel-update-order-status',
      'systems-integrations:test-channel-record-invoice',
      'systems-integrations:test-channel-set-invoicing-behavior',
      'systems-integrations:test-channel-logistics-shipment-update',
      'systems-integrations:test-channel-poll-shipment-statuses'
    ]
  },

  'sales-channels:integrations:provision': {
    key: 'sales-channels:integrations:provision',
    resource: 'sales-channel-integrations',
    action: 'provision',
    label: 'Provision sales channel integrations',
    category: 'Integrations',
    sensitive: true,
    operations: [
      'systems-integrations:provision-system-integration-connector',
      'systems-integrations:provision-system-integration',
      'systems-integrations:link-mercadolibre-from-login'
    ]
  },

  'sales-channels:integrations:configure': {
    key: 'sales-channels:integrations:configure',
    resource: 'sales-channel-integrations',
    action: 'configure',
    label: 'Configure sales channel integrations',
    category: 'Integrations',
    operations: [
      'systems-integrations:update-system-integration-connector-configuration',
      'systems-integrations:update-system-integration-configuration',
      'systems-integrations:change-system-integration-connector-state',
      'systems-integrations:change-system-integration-state',
      'systems-integrations:request-systems-integrations-operation',
      'systems-integrations:confirm-systems-integrations-operation',
      'systems-integrations:request-systems-integrations-element-operation',
      'systems-integrations:confirm-systems-integrations-element-operation',
      'systems-integrations:map-channel-category',
      'systems-integrations:unmap-channel-category',
      // TV1-2113 D6: records that a channel category is a merchandising collection
      // ("Ofertas", "Novedades") with no correct Teselly category. Sits with map/unmap
      // because it is the same decision by the same operator — the third answer to
      // "what does this category correspond to?", not a separate power.
      'systems-integrations:mark-channel-category-unmappable',
      'systems-integrations:sync-channel-categories',
      'systems-integrations:request-categories-resync',
      'systems-integrations:systems-integrations-poll-shipments',
      'systems-integrations:systems-integrations-poll-publications',
      // ERP-ONBOARDING slice 5 — the ERP catalog import trigger, RE-HOMED here from
      // `sales-channels:webhooks:internal` (TV1-2108). It is no longer an
      // in-process-only seam: it is the ERP Items tab's operator action, so it belongs
      // to the capability that already governs "configure this integration" — beside
      // `request-categories-resync`, `poll-shipments` and `poll-publications`, the same
      // "ask this integration to do its thing" family. An operation belongs to exactly
      // ONE capability, so this is a move, not a second grant.
      //
      // GRANT RESOLUTION IS UNCHANGED for all three system roles — this move does not
      // widen or narrow reach, it only makes ownership truthful. `matchCapabilities`
      // expands a `subdomain:*` grant over every registered leaf with that prefix,
      // `systemOwned: true` included (`matchCapabilities.ts` has no such filter, and
      // `permissions.ts`'s `capabilities` field is documented as the internal view),
      // so account-owner (`sales-channels:*` → `['all']`) and manager
      // (idem) could already reach the operation under its old home, and operator
      // resolves `null` under BOTH homes (its `sales-channels:*` grant is `['own']`,
      // which intersects the `sales-channel-integrations` resource's
      // `['assigned','all']` scopes to `[]`). The operator-reach question is a real one,
      // but it is orthogonal to this move and is carried as an explicit decision.
      'systems-integrations:request-erp-bulk-import',
      // ERP-ONBOARDING slice 7 — the ERP CONTAINER IMPORT confirm. Same family as
      // `request-erp-bulk-import` above and gated identically from the ERP
      // configuration view: it configures THIS integration (it writes the Warehouse
      // Mapping onto its `settings`, the same surface
      // `update-system-integration-configuration` writes).
      //
      // Worth stating plainly: the confirm ALSO creates Teselly warehouses, via the
      // in-process client library, which does not re-enter the gateway. So this
      // capability transitively authorizes depot creation for the chosen containers.
      // That is the same property every in-process cross-module write in this repo
      // has (the ERP item import creates ITEMS under this very key), not something
      // this slice introduces — but it is the reason the confirm is gated on a
      // configure capability rather than a read one.
      'systems-integrations:erp-container-import-warehouse-import',
      // ERP-ONBOARDING slice 8 — the PRICE LIST half of the same Container Import
      // confirm, gated identically and for the same reason: it configures THIS
      // integration (it writes the Price List Mapping onto its `settings`, the same
      // surface `update-system-integration-configuration` writes).
      //
      // And, exactly as with the warehouse import, the confirm ALSO creates Teselly
      // price lists via the in-process client library, which does not re-enter the
      // gateway — so this capability transitively authorizes price-list creation for the
      // chosen containers. Inherited property of every in-process cross-module write in
      // this repo, not something this slice introduces, and the reason the confirm is
      // gated on a configure capability rather than a read one.
      'systems-integrations:erp-container-import-price-list-import',
      // ERP-ONBOARDING slice 9 — the STOCK SWEEP confirm. Same family and the same
      // gate as the two container imports above: it is the ERP configuration view's
      // "import stock from this integration" action, and it acts on THIS integration's
      // configured scope (its Warehouse Mapping).
      //
      // Two properties worth stating rather than leaving implicit:
      //   - it transitively authorizes a WRITE OVER TESELLY STOCK for every mapped
      //     warehouse, via the in-process client library (which does not re-enter the
      //     gateway) — the same property the container imports have for warehouse /
      //     price-list creation, and the reason this is a configure capability, not a
      //     read one;
      //   - the sweep bypasses the rules engine, so it overrides the configured sync
      //     DIRECTION (ADR-190 D4): "import stock" writes Odoo's numbers into Teselly
      //     even where the inbound rule is off. Deliberate for onboarding (O8), and
      //     surfaced in the confirm copy.
      'systems-integrations:erp-stock-import-stock-import',
      // ERP-ONBOARDING slice 10 — the PRICE SWEEP confirm. Same family and the same gate
      // as the container imports and the stock sweep above: it is the ERP configuration
      // view's "import prices from this integration" action, acting on THIS integration's
      // configured scope (its Price List Mapping).
      //
      // Two properties worth stating rather than leaving implicit:
      //   - it transitively authorizes a WRITE OVER TESELLY PRICES for every mapped price
      //     list, via the in-process client library (which does not re-enter the gateway)
      //     — the same property the container imports have, and the reason this is a
      //     configure capability, not a read one;
      //   - the sweep bypasses the rules engine, so it overrides the configured sync
      //     DIRECTION (ADR-190 D4): "import prices" writes Odoo's numbers into Teselly
      //     even where the inbound rule is off. Deliberate for onboarding (O8), and
      //     surfaced in the confirm copy.
      'systems-integrations:erp-price-import-price-import',
      // TV1-2146 — the ERP DRIFT RE-CHECK. Same family and the same gate as the
      // container imports and the two sweeps above: it is an ERP configuration-view
      // operator action on THIS integration, reached from the drift audit tab.
      //
      // Narrower than its neighbours in what it authorizes, and deliberately so: it
      // writes NO Teselly domain state and NO mirror row (ADR-069 D5). Its only
      // downstream effect is an external-observation capture, which the existing
      // projector and drift-evaluation policy consume. What it DOES authorize is an
      // authenticated point-read of the ERP on demand — the same class of act as the
      // reads the neighbouring imports perform, which is why it joins this bag rather
      // than earning a capability of its own.
      'systems-integrations:erp-drift-recheck'
    ]
  },

  'sales-channels:integrations:disconnect': {
    key: 'sales-channels:integrations:disconnect',
    resource: 'sales-channel-integrations',
    action: 'disconnect',
    label: 'Disconnect sales channel integrations',
    category: 'Integrations',
    sensitive: true,
    operations: [
      'systems-integrations:channel-import-preview-discard',
      'systems-integrations:channel-import-preview-stop'
    ]
  },

  // TV1-1897: sales-channel-integrations are a DELEGATABLE resource
  // (principal-set model, array `assignedTo`). This capability grants
  // assign/unassign of a channel integration to principals — users or groups —
  // driving the `assigned` scope. A channel's principal set is also folded
  // live into the actor's `assignedScopes` to give transitive item/publication
  // visibility (SDD §5). Mirrors the warehouses assign template (TV1-1881).
  // Maps the two assign/unassign command operations emitted by the
  // SystemsIntegrationsAggregate ( AssignIntegrationAssignee /
  // RemoveIntegrationAssignee ).
  'sales-channels:integrations:assign': {
    key: 'sales-channels:integrations:assign',
    resource: 'sales-channel-integrations',
    action: 'assign',
    label: 'Assign sales channel integrations to users or groups',
    category: 'Integrations',
    operations: [
      'systems-integrations:assign-integration-assignee',
      'systems-integrations:remove-integration-assignee'
    ],
    implies: ['sales-channels:read']
  },

  //*******************************************
  // Automation (user-assignable)
  //*******************************************

  'automation:rules:read': {
    key: 'automation:rules:read',
    resource: 'rules',
    action: 'read',
    label: 'Read rules',
    category: 'Automation',
    operations: [
      'automation:rules',
      'automation:rules-readmodel',
      // TV1-2392 — the umbral de stock projection. pv3 auto-registers a default query
      // route for every readmodel, projector-only ones included (same shape as
      // `stock-management:stock-analytics-readmodel`), so the operation exists whether or
      // not anything reads it. It carries the seller's own threshold declarations, so it
      // sits under the rules READ capability rather than earning one of its own.
      'automation:stock-thresholds-readmodel'
    ]
  },

  'automation:rules:update': {
    key: 'automation:rules:update',
    resource: 'rules',
    action: 'update',
    label: 'Create or update rules',
    category: 'Automation',
    operations: [
      'automation:set-rule',
      'automation:set-rules',
      'automation:rules-draft',
      'automation:rules-dry-run',
      // TV1-2383 — taking a rule out of shadow. Grouped with rule UPDATE and not given a
      // capability of its own on purpose: capability gating is not an authority control
      // here (a new capability is handed to every default role, so a dedicated
      // `automation:rules:promote` would GRANT promotion to roles that cannot currently
      // edit a rule at all — strictly wider, not narrower). Anyone who may change what a
      // rule does may also stop observing it; the real guarantee is the aggregate's
      // one-way invariant, which no capability can bypass.
      'automation:unshadow-rule',
      // TV1-2453 — change a bound rule's declared input values without restarting its
      // observation. Meant to be issued only by the Sub-Goal assignation policy (a later slice);
      // internal-only route (scripts/generateRouteAuthConfig.ts INTERNAL_ONLY_COMMAND_ROUTES,
      // policy-issued), same grant family as
      // `automation:unshadow-rule`, for the same reason (a dedicated key would be wider).
      'automation:set-rule-declared-inputs-values',
      // TV1-2377 slice 3 — the POLICY artifact that binds an approved Sub-Goal's shadow rule.
      // pv3 surfaces every artifact as an operation, so it needs coverage or `generate-all`
      // fails; homed HERE, with the `SetRule` it issues, per ADR-153's grouping rule.
      //
      // ⚠️ THE CAPABILITY IS NOT WHAT PROTECTS IT — `automation:*` is a wildcard grant that
      // expands over every registered leaf. What keeps a seller from binding an AI-authored
      // rule herself is that this is an in-process policy hop with no gateway route, reachable
      // only from `SubGoalApproved`, which `requireAdminReviewer()` gates.
      'automation:sub-goal-rule-assignation',
      // TV1-2377 slice 6 — the POLICY artifact that takes that same shadow rule LIVE on
      // promotion. Homed with `automation:unshadow-rule`, the one command it issues, for the
      // reason recorded above: a dedicated key would be a WIDER grant, not a narrower one.
      //
      // ⚠️ Same non-protection as its sibling: internal, no gateway route (route-config marks it
      // `visibility: "internal"`), reachable only from `SubGoalPromotionRecorded`. The authority
      // control is the aggregate's one-way promotion invariant, not this entry.
      'automation:sub-goal-promotion-unshadow'
    ],
    implies: ['automation:rules:read']
  },

  'automation:rules:delete': {
    key: 'automation:rules:delete',
    resource: 'rules',
    action: 'delete',
    label: 'Delete rules',
    category: 'Automation',
    operations: [
      'automation:remove-rule'
    ],
    implies: ['automation:rules:read']
  },

  'automation:rule-definitions:read': {
    key: 'automation:rule-definitions:read',
    resource: 'rule-definitions',
    action: 'read',
    label: 'Read rule definitions',
    category: 'Automation',
    operations: [
      'automation:rule-definitions',
      'automation:rules-definitions-readmodel'
    ]
  },

  'automation:rule-definitions:create': {
    key: 'automation:rule-definitions:create',
    resource: 'rule-definitions',
    action: 'create',
    label: 'Create rule definitions',
    category: 'Automation',
    sensitive: true,
    operations: [
      'automation:define-rule-definition',
      // TV1-2377 slice 3 — the POLICY artifact that mints an AI-authored definition on
      // approval (decision D1). Same grouping rule and same caveat as
      // `automation:sub-goal-rule-assignation` above: the capability is coverage, not the
      // control; the control is that the only trigger is an admin reviewer's approval.
      'automation:composed-sub-goal-definition'
    ],
    implies: ['automation:rule-definitions:read']
  },

  'automation:rule-definitions:delete': {
    key: 'automation:rule-definitions:delete',
    resource: 'rule-definitions',
    action: 'delete',
    label: 'Delete rule definitions',
    category: 'Automation',
    sensitive: true,
    operations: [
      'automation:delete-rule-definition'
    ],
    implies: ['automation:rule-definitions:read']
  },

  'automation:executions:read': {
    key: 'automation:executions:read',
    resource: 'rule-executions',
    action: 'read',
    label: 'Read rule executions',
    category: 'Automation',
    operations: [
      'automation:rule-executions',
      'automation:rule-executions-search',
      'automation:rule-executions-status-counts',
      'automation:rule-execution-batches',
      'automation:rule-execution-batches-search',
      'automation:rules-executions-readmodel',
      'automation:rules-execution-batches-readmodel'
    ]
  },

  'automation:executions:retry': {
    key: 'automation:executions:retry',
    resource: 'rule-executions',
    action: 'retry',
    label: 'Retry rule executions',
    category: 'Automation',
    operations: [
      'automation:retry-rule-execution'
    ]
  },

  // TV1-2081 — the reconcile re-drive trigger (POST /rule-executions/re-evaluate).
  // Re-evaluates current-state-safe rules for a publication from current readmodel
  // state, producing rule executions. A DISTINCT capability from `:retry` (which re-runs
  // ONE already-failed execution) because this MINTS executions across a publication's
  // rules — a different action on the same `rule-executions` resource. Covered for
  // account-owner/manager/operator by the existing `automation:*` wildcard.
  'automation:executions:re-evaluate': {
    key: 'automation:executions:re-evaluate',
    resource: 'rule-executions',
    action: 're-evaluate',
    label: 'Re-evaluate rules from current state',
    category: 'Automation',
    operations: [
      'automation:re-evaluate-publication-rules'
    ]
  },

  //*******************************************
  // Automation Requests (TV1-2378 / TV1-2377, ADR-194)
  //
  // An Automation Request is a seller INTENT decomposed into Sub-Goals, composed by an LLM and
  // gated by a developer reviewer. The keys are split by WHO acts, not by CRUD shape, because
  // three different principals touch this one aggregate: the seller submits and withdraws, the
  // COMPOSER POLICY records outcomes, and an internal reviewer approves or rejects.
  //
  // ⚠️ `automation:rules:update` is deliberately NOT reused for the review lane. That key is
  // held by ordinary sellers, so reusing it would let any seller approve an AI-authored rule
  // into their own account — which is the one thing the reviewer gate exists to prevent.
  //
  //*******************************************

  // THE SELLER'S READ LANE (TV1-2379/B). Declared HERE, with its operations, rather than by
  // TV1-2378/A: a `:read` key ahead of the readmodel would have carried an EMPTY operations
  // list — grantable in the UI and covering nothing, the ADR-153 failure mode.
  //
  // It covers the SELLER surface only. The dev review queue, the outcome counters and the gap
  // backlog are CROSS-ACCOUNT internal routes and own NO capability at all: they are
  // `.withSubdomain( '' )`, so gateway Step 5b admits `userType === 'admin'` and 403s everyone
  // else. Giving them a capability here would be a PRIVILEGE ESCALATION, not a tightening —
  // a real `x-subdomain` makes Step 5b stop firing and reroutes them through org RBAC, where
  // default system roles hold `automation:*` by wildcard and `matchCapabilities` expands it
  // over every registered leaf. That is the same finding `requireAdminReviewer` documents for
  // the write side.
  'automation:automation-requests:read': {
    key: 'automation:automation-requests:read',
    resource: 'automation-requests',
    action: 'read',
    label: 'View automation requests',
    category: 'Automation',
    operations: [
      'automation:automation-requests',
      'automation:automation-requests-summary',
      'automation:automation-requests-readmodel'
    ]
  },

  'automation:automation-requests:submit': {
    key: 'automation:automation-requests:submit',
    resource: 'automation-requests',
    action: 'submit',
    label: 'Submit automation requests',
    category: 'Automation',
    operations: [
      'automation:submit-automation-request'
    ],
    // A seller who may act on her requests must be able to SEE them — mirrors
    // `automation:rules:update` implying `automation:rules:read`.
    implies: ['automation:automation-requests:read']
  },

  'automation:automation-requests:withdraw': {
    key: 'automation:automation-requests:withdraw',
    resource: 'automation-requests',
    action: 'withdraw',
    label: 'Withdraw automation requests',
    category: 'Automation',
    operations: [
      'automation:withdraw-automation-request'
    ],
    // A seller who may act on her requests must be able to SEE them — mirrors
    // `automation:rules:update` implying `automation:rules:read`.
    implies: ['automation:automation-requests:read']
  },

  // The SELLER's go-live decision: a declarative sub-goal enters shadow immediately and a
  // developer approves it, but only the seller promotes it to live. Separate from `:submit`
  // because approving a shadow rule to run for real is a different consent than asking for one.
  'automation:automation-requests:promote': {
    key: 'automation:automation-requests:promote',
    resource: 'automation-requests',
    action: 'promote',
    label: 'Promote a reviewed rule to live',
    category: 'Automation',
    operations: [
      'automation:record-sub-goal-promoted',
      // TV1-2453 D5: the seller records declared input values IN ORDER to promote a rule whose
      // input is empty (ADR-201), so it is the same consent as promotion, not a new one.
      'automation:record-sub-goal-input-values'
    ],
    // A seller who may act on her requests must be able to SEE them — mirrors
    // `automation:rules:update` implying `automation:rules:read`.
    implies: ['automation:automation-requests:read']
  },

  // THE REVIEW LANE. Held by internal developers (`userType: 'admin'`), never by a seller.
  // `sensitive: true` because the holder rules on rules that will run inside a customer's
  // account, and because `approve-with-edits` lets the holder rewrite the graph outright.
  'automation:automation-requests:review': {
    key: 'automation:automation-requests:review',
    resource: 'automation-requests',
    action: 'review',
    label: 'Review AI-composed automation sub-goals',
    category: 'Automation',
    sensitive: true,
    operations: [
      'automation:approve-sub-goal',
      'automation:reject-sub-goal'
    ]
  },

  // THE COMPOSER POLICY's own key — TV1-2380/C's machine principal, never granted to a human.
  // These three commands are how the composer REPORTS what it found; a seller holding them
  // could forge a "the model composed this" outcome (and, through TV1-2388/K, poison the eval
  // corpus). Grouping policy-dispatched operations under their own capability follows ADR-153.
  'automation:automation-requests:record-outcomes': {
    key: 'automation:automation-requests:record-outcomes',
    resource: 'automation-requests',
    action: 'record-outcomes',
    label: 'Record automation sub-goal composition outcomes',
    category: 'Automation',
    sensitive: true,
    operations: [
      'automation:record-sub-goal-composed',
      'automation:record-sub-goal-unsatisfiable',
      'automation:record-sub-goal-composition-attempt-failed',
      // TV1-2380/C — the two POLICY artifacts themselves. pv3 surfaces every artifact as an
      // operation, so they need coverage or `generate-all` fails; they are homed HERE rather
      // than under a new key because they are the same machine principal doing the same thing
      // as the three `record-…` commands above (ADR-153's grouping rule).
      //
      // ⚠️ THE CAPABILITY IS NOT WHAT PROTECTS THEM. `automation:*` is a WILDCARD grant that
      // `matchCapabilities` expands over every registered leaf, `systemOwned` included, so an
      // account owner holds this key whatever it is marked. What actually keeps a seller from
      // forging a composition outcome is that these are in-process policy hops with no
      // gateway route, plus TV1-2378/A's `requireOwnAccountState()` runtime invariant on the
      // aggregate — never this entry.
      'automation:compose-sub-goal',
      'automation:compose-sub-goal-retry-sweep'
    ]
  },

  //*******************************************
  // Analytics (user-assignable)
  //*******************************************

  'analytics:layouts:read': {
    key: 'analytics:layouts:read',
    resource: 'analytics-layouts',
    action: 'read',
    label: 'Read analytics layouts',
    category: 'Analytics',
    operations: [
      'analytics:ui-widget-layouts-readmodel'
    ]
  },

  'analytics:layouts:create': {
    key: 'analytics:layouts:create',
    resource: 'analytics-layouts',
    action: 'create',
    label: 'Create analytics layouts',
    category: 'Analytics',
    operations: [
      'analytics:add-ui-widget-layout'
    ],
    implies: ['analytics:layouts:read']
  },

  'analytics:layouts:update': {
    key: 'analytics:layouts:update',
    resource: 'analytics-layouts',
    action: 'update',
    label: 'Update analytics layouts',
    category: 'Analytics',
    operations: [
      'analytics:change-ui-widget-layout',
      'analytics:activate-ui-widget-layout',
      'analytics:deactivate-ui-widget-layout'
    ],
    implies: ['analytics:layouts:read']
  },

  'analytics:layouts:delete': {
    key: 'analytics:layouts:delete',
    resource: 'analytics-layouts',
    action: 'delete',
    label: 'Delete analytics layouts',
    category: 'Analytics',
    operations: [
      'analytics:remove-ui-widget-layout'
    ],
    implies: ['analytics:layouts:read']
  },

  // TV1-1937 / ADR-051 — LLM report generation (POST /analytics/generate-report).
  //
  // A DISTINCT capability rather than a reuse of `analytics:layouts:create`, for one
  // concrete reason: this operation is COST-BEARING (it burns model tokens on every
  // call, on a user-facing request path). A separate capability is the only place we
  // can later meter it, rate-limit it, or gate it by plan tier WITHOUT also revoking
  // the manual widget builder — which is free, and which every user who can build a
  // report by hand should keep.
  //
  // It `implies` `analytics:layouts:read`: a generated widget is only useful to
  // someone who can see layouts, and the FE surfaces it from the layouts screen.
  // It does NOT imply `analytics:layouts:create` — generation is return-only
  // (no persistence); saving the result still goes through `AddUiWidgetLayout` and
  // is gated by `analytics:layouts:create` on its own merits.
  'analytics:reports:generate': {
    key: 'analytics:reports:generate',
    resource: 'analytics-layouts',
    action: 'generate-report',
    label: 'Generate analytics reports with AI',
    category: 'Analytics',
    operations: [
      'analytics:generate-report'
    ],
    implies: ['analytics:layouts:read']
  },

  // TV1-2393 slice 7 — the OBSERVED OPERATIONS fact projection
  // (`analytics_observed_operations`). pv3 auto-registers a default query route for every
  // readmodel, projector-only ones included (same shape as
  // `stock-management:stock-analytics-readmodel` and `automation:stock-thresholds-readmodel`),
  // so the operation exists whether or not anything reads it yet — slice 8's report is what
  // will.
  //
  // A DISTINCT capability rather than a fold into `analytics:layouts:read`, for one concrete
  // reason: this projection is the AUDIT of tenant-behalf egress Teselly SUPPRESSED, and it
  // records suspension-driven suppression alongside Observation Mode (D11). Folding it into
  // layout-viewing would grant every user who can open a dashboard a read over the record of
  // what Teselly withheld from their account and why. That is a separate decision from "may
  // see analytics layouts", so it gets a separate switch. It reuses the `analytics-layouts`
  // RESOURCE with its own ACTION, exactly as `analytics:reports:generate` above does — the
  // capability is the grantable unit, and both are org-global tenant-`all` config.
  //
  // It `implies` `analytics:layouts:read` on the same reasoning the report capability above
  // does: the records are only useful to someone who can see the analytics surface that
  // renders them.
  'analytics:observed-operations:read': {
    key: 'analytics:observed-operations:read',
    resource: 'analytics-layouts',
    action: 'read-observed-operations',
    label: 'Read observed (suppressed) operations',
    category: 'Analytics',
    operations: [
      'analytics:observed-operations-readmodel'
    ],
    implies: ['analytics:layouts:read']
  },

  //*******************************************
  // Marketplace Health (user-assignable)
  //*******************************************
  // TV1-1924 / S9 — the Seller Reputation Health Sync. Reputation is an
  // externally-owned, per-channel health fact with no per-row delegation, so every
  // capability here anchors on the tenant-global `sales-channels` resource (scope
  // `all`). No subdomain wildcard is registered: `ALL_SUBDOMAIN_WILDCARDS`
  // (roles-and-permissions.ts) is a fixed 16-entry list asserted by tests, and
  // granting these to a system role is a separate authz decision — this ticket only
  // closes the generation/registry gap so every marketplace-health route operation
  // is owned (drift-free) and the catalog is authorizable.

  // Read of the projected Seller Reputation facts (auto-generated readmodel GET
  // query). Mirrors the `analytics:layouts:read` / `sales:analytics:read` fact-read
  // pattern.
  'marketplace-health:reputation:read': {
    key: 'marketplace-health:reputation:read',
    resource: 'sales-channels',
    action: 'read-reputation',
    label: 'Read Seller Reputation',
    category: 'Marketplace Health',
    operations: [
      'marketplace-health:seller-reputation-readmodel'
    ]
  },

  // TV1-1925 / S10 — read of the projected Listing Visits facts (the traffic
  // denominator behind Conversion). Its OWN capability rather than being folded into
  // `reputation:read`: visits are a distinct signal at a distinct grain (per listing,
  // per day), and borrowing another capability's gate for authorization convenience
  // is exactly the drift this registry exists to prevent. Covered for account-owner
  // by the existing `marketplace-health:*` grant, so no new role grant is needed.
  'marketplace-health:visits:read': {
    key: 'marketplace-health:visits:read',
    resource: 'sales-channels',
    action: 'read-listing-visits',
    label: 'Read Listing Visits',
    category: 'Marketplace Health',
    operations: [
      'marketplace-health:listing-visits-readmodel'
    ]
  },

  // The manual Force Sync trigger (POST /marketplace-health/:connectorId/force-sync),
  // mirroring the systems-integrations `poll-shipments` user-facing trigger. TV1-1925
  // widened it from reputation-only to a full Health Sync (reputation + visits); the
  // route — and therefore this operation and its key — are unchanged.
  'marketplace-health:reputation:sync': {
    key: 'marketplace-health:reputation:sync',
    resource: 'sales-channels',
    action: 'sync-reputation',
    label: 'Force Marketplace Health sync',
    category: 'Marketplace Health',
    operations: [
      'marketplace-health:marketplace-health-force-sync'
    ]
  },

  //*******************************************
  // Notifications (user-assignable)
  //*******************************************

  'notifications:read': {
    key: 'notifications:read',
    resource: 'notifications',
    action: 'read',
    label: 'Read notifications',
    category: 'Notifications',
    operations: [
      'notifications:notifications',
      'notifications:notifications-readmodel',
      'notifications:notifications-count',
      'notifications:mark-notification-as-read',
      'notifications:mark-notification-as-unread'
    ]
  },

  'notifications:send': {
    key: 'notifications:send',
    resource: 'notifications',
    action: 'send',
    label: 'Send notifications',
    category: 'Notifications',
    sensitive: true,
    operations: [
      'notifications:send-user-notification'
    ]
  },

  //*******************************************
  // Shipping (user-assignable)
  //*******************************************

  // TV1-1895: the bespoke `shipping:retrieve-shipment-label` operation was removed
  // (label creation/retrieval is now an integrations operation). This stays as a
  // UI-only gate for the order "create shipment / print label" affordance; its
  // backend enforcement lives in the systems-integrations dispatcher + registry.
  'shipping:labels:retrieve': {
    key: 'shipping:labels:retrieve',
    resource: 'shipping-carriers',
    action: 'retrieve-label',
    label: 'Retrieve shipping labels',
    category: 'Shipping',
    operations: []
  },

  //*******************************************
  // Feature Flags (user-assignable)
  //*******************************************

  'feature-flags:read': {
    key: 'feature-flags:read',
    resource: 'feature-flags',
    action: 'read',
    label: 'Read feature flags',
    category: 'Operations',
    operations: [
      'feature-flags:feature-flags',
      'feature-flags:feature-flags-readmodel'
    ]
  },

  // Baseline session-bootstrap read, NOT flag administration: every
  // authenticated user's frontend OpenFeature provider resolves its OWN
  // account's flags via `/org-resolved-flags` (and `/public-flags` pre-login).
  // Granted to every system role (account-owner via the `feature-flags:*`
  // wildcard; manager/operator explicitly) so the app loads for all roles.
  // Mirrors `identity:self:read`. Kept separate from `feature-flags:read`
  // (the admin "manage flag definitions" surface) so granting self-read never
  // implies flag-management power. (org-featureflags 403 fix)
  'feature-flags:self:read': {
    key: 'feature-flags:self:read',
    resource: 'feature-flags',
    action: 'read-self',
    label: 'Read own resolved feature flags',
    category: 'Operations',
    operations: [
      'feature-flags:org-resolved-flags',
      'feature-flags:public-flags'
    ]
  },

  'feature-flags:create': {
    key: 'feature-flags:create',
    resource: 'feature-flags',
    action: 'create',
    label: 'Create feature flags',
    category: 'Operations',
    sensitive: true,
    operations: [
      'feature-flags:create-feature-flag',
      'feature-flags:import-feature-flags'
    ],
    implies: ['feature-flags:read']
  },

  'feature-flags:update': {
    key: 'feature-flags:update',
    resource: 'feature-flags',
    action: 'update',
    label: 'Update feature flags (edit, archive, enable, schedule)',
    category: 'Operations',
    sensitive: true,
    operations: [
      'feature-flags:update-feature-flag',
      'feature-flags:archive-feature-flag',
      'feature-flags:enable-feature-flag',
      'feature-flags:disable-feature-flag',
      'feature-flags:schedule-feature-flag'
    ],
    implies: ['feature-flags:read']
  },

  //*******************************************
  // Messaging (user-assignable)
  //*******************************************

  'sales:messaging:questions:read': {
    key: 'sales:messaging:questions:read',
    resource: 'channel-questions',
    action: 'read',
    label: 'Read buyer messaging questions',
    category: 'Messaging',
    operations: [
      'sales:questions',
      'sales:questions-readmodel'
    ]
  },

  'sales:messaging:questions:answer': {
    key: 'sales:messaging:questions:answer',
    resource: 'channel-questions',
    action: 'answer',
    label: 'Answer buyer messaging questions',
    category: 'Messaging',
    operations: [
      'sales:answer-channel-question'
    ]
  },

  // TV1-1993 — trigger a full re-pull (backfill) of a channel's questions. A
  // dedicated action so "backfill" is separately grantable from read/answer. Maps
  // to the Force-Sync endpoint operation (`sales:crm-force-sync-questions`, the slug
  // of its endpoint name per ADR-057).
  'sales:messaging:questions:sync': {
    key: 'sales:messaging:questions:sync',
    resource: 'channel-questions',
    action: 'sync',
    label: 'Sync (backfill) buyer messaging questions',
    category: 'Messaging',
    operations: [
      'sales:crm-force-sync-questions'
    ]
  },

  // TV1-2259 slice 4 — read POST-SALE channel messages (the buyer conversation on an
  // order/pack thread), a separate grant from the PRE-sale questions read: the two are
  // different rows, different projections and, in practice, different audiences.
  //
  // SCOPE ANCHOR: its OWN resource `channel-messages` (TV1-2259) — the enforcement
  // SHAPE is identical to `channel-questions` (transitive, channel-anchored on
  // `channelIntegrationId`, read-side only), but the two are different rows with
  // different audiences, so they get separate resource keys and are separately
  // grantable at the resource tier. Not `inbound-messages` / `outbound-messages`:
  // those scope on `ownerId`, and nobody in Teselly OWNS a buyer's message.
  // The read-side transformer in `channelMessages.readmodel.ts` passes THIS
  // capability plus `assignedToField: 'channelIntegrationId'` — matching this
  // resource's `assigned` predicate exactly (ADR-089 §6.5.0).
  // FE reachability: the Interactions section gate and route guard admit an ANY-OF of the
  // section key plus every tab key (`sectionGateCapabilities` in web-app's
  // `navigation.config.ts`), so this grant alone reaches the messages tab — pinned by
  // `web-app/src/config/__tests__/navigation.capabilityGates.test.ts`. [TV1-2259]
  'sales:messaging:messages:read': {
    key: 'sales:messaging:messages:read',
    resource: 'channel-messages',
    action: 'read',
    label: 'Read post-sale channel messages',
    category: 'Messaging',
    operations: [
      'sales:messages',
      'sales:channel-messages-readmodel',
      // TV1-2259 slice 6 — the OUTBOUND rows (`sales_outbound_channel_messages_readmodel`:
      // what WE sent, and what the channel REFUSED). Homed in the same `:read` capability
      // as the inbound inbox: one resource family, one read grant. They are a separate
      // COLLECTION so the buyer-message inbox query cannot return our own sends, not a
      // separate authority — anyone who may read the conversation may read both halves.
      'sales:outbound-messages',
      'sales:outbound-channel-messages-readmodel'
    ]
  },

  // TV1-2259 slice 6 — SEND a post-sale channel message by naming an option from the
  // channel's closed `action_guide` menu. Separate from `:read` because sending is an
  // irreversible outward act on the seller's reputation, and separate from
  // `:send-free-text` below because of the cap (see there).
  //
  // Grants ONLY the MENU command. `SendFreeTextChannelMessage` is deliberately NOT listed
  // here: this capability is the one an automation/agent path is given. That keeps the
  // free-text slot out of reach OF THIS GRANT — it does NOT make the slot unreachable from
  // automation: `sales:messaging:*` is in `ALL_SUBDOMAIN_WILDCARDS`, so a default-role
  // operator holds `:send-free-text` regardless, and a human-authored rule can dispatch it
  // through the auto-generated `SendFreeTextChannelMessageNode`. ADR-141 §2.
  'sales:messaging:messages:send': {
    key: 'sales:messaging:messages:send',
    resource: 'channel-messages',
    action: 'send',
    label: 'Send post-sale channel messages (menu options)',
    category: 'Messaging',
    operations: [
      'sales:send-channel-message'
    ],
    implies: [
      'sales:messaging:messages:read'
    ]
  },

  // TV1-2259 slice 6 — SPEND the pack's ONE 350-char free-text slot (`option_id: OTHER`).
  // Its own capability and flagged `sensitive`, because the slot's `cap_available` goes
  // 1 → 0 on first use and never returns: spending it is an irreversible, per-order-unique
  // act that a HUMAN should choose. Implies `:send` — anyone trusted with the scarce slot
  // can obviously pick a menu option.
  //
  // ⚠️ `sensitive: true` IS NOT ENFORCED ANYWHERE — it is a label appearing only in
  // comments and tests. And `sales:messaging:*` is in `ALL_SUBDOMAIN_WILDCARDS`, so the
  // DEFAULT role grants this capability. Narrowing it is a role-grant decision that is
  // deliberately NOT taken here. ADR-141 §2.
  'sales:messaging:messages:send-free-text': {
    key: 'sales:messaging:messages:send-free-text',
    resource: 'channel-messages',
    action: 'send-free-text',
    label: 'Spend a pack\'s single free-text message slot',
    category: 'Messaging',
    sensitive: true,
    operations: [
      'sales:send-free-text-channel-message'
    ],
    implies: [
      'sales:messaging:messages:send'
    ]
  },

  //*******************************************
  // Operations — domain-operations layer (TV1-1828)
  //*******************************************

  // Read the run lifecycle + the computed diff payload.
  'operations:runs:read': {
    key: 'operations:runs:read',
    resource: 'operation-runs',
    action: 'read',
    label: 'Read operation runs',
    category: 'Operations',
    operations: [
      'operations:runs',
      'operations:runs-search',
      'operations:change-sets'
    ]
  },

  // Propose a domain operation (compute a previewable change set). Both the
  // friendly `/operations/:type/propose` entry point and the aggregate command
  // endpoint are named `ProposeOperation`, so under ADR-057 (operation key =
  // endpoint-name slug) they both resolve to the single `propose-operation` key.
  'operations:runs:propose': {
    key: 'operations:runs:propose',
    resource: 'operation-runs',
    action: 'propose',
    label: 'Propose operations',
    category: 'Operations',
    operations: [
      'operations:propose-operation'
    ],
    implies: [
      'operations:runs:read'
    ]
  },

  // Approve / reject / amend a computed change set. Approving an
  // external-irreversible operation triggers (token-gated) channel egress, so
  // this is sensitive. `AmendChangeItem` (ADR-190) is a review-time edit — the
  // reviewer who can approve/reject can also amend a proposed change item's
  // cells (re-hashed atomically) before approving.
  'operations:runs:approve': {
    key: 'operations:runs:approve',
    resource: 'operation-runs',
    action: 'approve',
    label: 'Approve or reject operations',
    category: 'Operations',
    sensitive: true,
    operations: [
      'operations:approve-operation',
      'operations:reject-operation',
      'operations:amend-change-item'
    ],
    implies: [
      'operations:runs:read'
    ]
  },

  //*******************************************
  // ML & AI (user-assignable)
  //*******************************************

  'ml-and-ai:assistant:use': {
    key: 'ml-and-ai:assistant:use',
    resource: 'conversations',
    action: 'use',
    label: 'Use AI assistant',
    category: 'AI',
    operations: [
      'ml-and-ai:add-user-message',
      'ml-and-ai:add-assistant-message',
      'ml-and-ai:conversations',
      'ml-and-ai:ai-assistant-send-message',
      // TV1-2382. All three hang off the SAME capability and the SAME
      // `conversations` resource, which is `own`-scoped on `ownerId`: a seller
      // reading her own chat's messages, and submitting a draft agreed in that
      // chat, is the same authority as using the assistant at all. Splitting
      // them would create a grant that can read a conversation but not the
      // artifact on it — a distinction with no product meaning and a new way to
      // fail closed.
      'ml-and-ai:conversation-messages',
      'ml-and-ai:conversation-messages-readmodel',
      'ml-and-ai:ai-assistant-submit-automation-request',
      // TV1-2417. Rating an answer is the same authority as having received it: the
      // route takes a trace id the seller could only have obtained by holding the
      // conversation, and writes one bounded integer to an observability sink. A
      // separate capability would be a grant that can use the assistant but not say
      // the answer was wrong — no product meaning, and one more way to fail closed on
      // exactly the feedback we most want.
      'ml-and-ai:ai-assistant-feedback'
    ]
  },

  'ml-and-ai:agents:run': {
    key: 'ml-and-ai:agents:run',
    resource: 'agent-runs',
    action: 'run',
    label: 'Run AI agents',
    category: 'AI',
    operations: [
      'ml-and-ai:start-agent-run',
      'ml-and-ai:resume-agent-run',
      'ml-and-ai:interrupt-agent-run',
      'ml-and-ai:finish-agent-run',
      'ml-and-ai:fail-agent-run'
    ]
  },

  //*******************************************
  // Internal capabilities (NOT user-assignable)
  //*******************************************
  // These exist to give every route-config operation an owner. They are
  // surfaced read-only in the role builder (SDD §8.4) and rejected by the
  // aggregate's R2 rule when present in a user-defined custom role.

  // ─── Identity internal ─────────────────────────────────────────────
  'identity:auth:anonymous': {
    key: 'identity:auth:anonymous',
    resource: 'auth-users',
    action: 'anonymous-auth',
    label: 'Anonymous auth endpoints',
    category: 'System',
    systemOwned: true,
    operations: [
      'identity:login',
      'identity:login-start',
      'identity:login-finish',
      'identity:login-with-password',
      'identity:login-with-magic-link',
      'identity:login-with-passkey',
      'identity:login-with-google-one-tap',
      'identity:sign-in',
      'identity:sign-in-with-google',
      'identity:sign-in-with-mercadolibre',
      'identity:social',
      'identity:complete-social-login',
      'identity:logout',
      'identity:logout-authentication-user',
      'identity:callback',
      'identity:passkey-register-start',
      'identity:passkey-register-finish',
      'identity:verify',
      'identity:send',
      'identity:authorize-request',
      // TV1-2321: internal identity resolution for the standalone
      // request-authorization pod. Same class as `authorize-request` — a
      // system-owned gateway endpoint. account-owner's `identity:*: ['all']`
      // wildcard DOES resolve this capability at the tier-count level (see
      // `operationTierDerivation.test.ts`'s 610->611 pin bump) — but it is
      // unreachable externally: `visibility: 'internal'` makes the gateway
      // reject the request before Step 2 credential extraction ever runs, so
      // the grant is never exercised.
      'identity:resolve-identity',
      'identity:get-token',
      'identity:oauth-access-token',
      'identity:jwks',
      'identity:permission-cache-invalidation',
      'identity:stream',
      'identity:apply-impersonation-session'
    ]
  },

  'identity:sync:internal': {
    key: 'identity:sync:internal',
    resource: 'auth-users',
    action: 'sync',
    label: 'Identity sync internals',
    category: 'System',
    systemOwned: true,
    operations: [
      'identity:identity-from-account-sync',
      'identity:identity-from-user-profile-sync',
      'identity:bind-active-account-after-registration',
      'identity:welcome-email-sending',
      // TV1-1865: assignment-group membership sync policy + the AuthUser
      // commands it issues (internal — never user-facing; the
      // AssignmentGroupMembershipSyncPolicy is the sole caller).
      'identity:assignment-group-membership-sync',
      'identity:assign-group-membership',
      'identity:remove-group-membership'
    ]
  },

  // ─── Clients-management internal ───────────────────────────────────
  'clients-management:sync:internal': {
    key: 'clients-management:sync:internal',
    resource: 'accounts',
    action: 'sync',
    label: 'Clients-management internals',
    category: 'System',
    systemOwned: true,
    operations: [
      'clients-management:account-registration-from-social-login',
      'clients-management:generate-app-login-url',
      // TV1-2105 D6: `NewAccountObservationDefaultPolicy` — the reaction that puts
      // every freshly registered account into observation mode. Internal; leaving
      // observation is an admin-panel-only action on the AccountObservation aggregate.
      'clients-management:new-account-observation-default',
      'clients-management:non-payment-suspension',
      // TV1-1752 (S6, ADR-126 / L-1) — `TerminationEgressStopPolicy`, the reaction
      // that turns `ClientAccountCancelled` into an ADMIN hold (never a payment
      // hold). Internal, `systemOwned`, never grantable to a tenant role.
      'clients-management:termination-egress-stop',
      // TV1-1865: assignment-group erasure policy (internal — reacts to
      // AccountDataErased to tear down the account's groups + memberships).
      'clients-management:assignment-group-erasure',
      'clients-management:stream',
      'clients-management:user-preferences-creation',
      'clients-management:user-profile-creation',
      // The policy that reacts to `SocialLoginCompleted`. Its COMMAND lives in
      // `user-profiles:system-backfill`, not here — the same split this file
      // already uses for `user-profile-creation` (policy, here) vs
      // `register-user-profile` (command, in `user-profiles:create`).
      'clients-management:user-profile-backfill-from-social-login',
      'clients-management:user-profile-removal'
    ]
  },

  // ─── Sales internal ────────────────────────────────────────────────
  'sales:webhooks:internal': {
    key: 'sales:webhooks:internal',
    resource: 'orders',
    action: 'webhooks',
    label: 'Sales webhook internals',
    category: 'System',
    systemOwned: true,
    operations: [
      'sales:stream',
      'sales:fail-placing-order',
      'sales:process-inbound-question',
      // TV1-2259 slice 4: `RecordChannelMessage`, dispatched ONLY by
      // `SystemsIntegrationsWebhookHandlerPolicy`'s `channel-message` branch — no browser
      // records a channel message. Homed in THIS capability, not `sales:messaging:internal`,
      // for the same reason `process-inbound-question` above is: it is an inbound-channel
      // capture on the sales side, and this is the capability the system principal already
      // holds (`systemPrincipals.ts`). Putting it in `sales:messaging:internal` compiles and
      // passes the coverage check, then fails the system-principal grant gate. The READ side
      // is the separately grantable `sales:messaging:messages:read`.
      'sales:record-channel-message',
      // ADR-049: AutoIssueReversalPolicy reacting to OrderPlaced (born reversed)
      // + OrderStatusChanged.
      'sales:auto-issue-reversal',
      // TV1-2205 / R12: written back onto the order by customerRegistration.policy.
      // Internal like `fail-placing-order` — no browser ever links a customer.
      'sales:link-order-customer',
      // TV1-2205 / R12: the policy's own event-handler POST route (it reacts to
      // OrderPlaced), same shape as `auto-issue-reversal` above.
      'sales:customer-registration',
      // Channel Order Seeding: `ChannelOrderImportPolicy`'s own event-handler POST
      // route (it reacts to `ChannelOrderImportBatchProcessStarted`) — the same shape
      // as `customer-registration` and `auto-issue-reversal` above, and internal for
      // the same reason: no browser calls it, the outbox does.
      //
      // Homed HERE and not in a sales capability of its own for the reason the
      // `record-channel-message` note above spells out — this is the capability the
      // system principal already holds (`systemPrincipals.ts:347`). A new capability
      // would compile and pass the coverage check, then fail the system-principal
      // grant gate at runtime.
      //
      // Note this is the POLICY's route. The USER-facing trigger is the separate
      // `batch-processes:start-channel-order-import-batch-process` under
      // `batch-processes:processes:start`.
      'sales:channel-order-import'
    ]
  },

  // ─── Sales/Messaging internal ──────────────────────────────────────────────────
  'sales:messaging:internal': {
    key: 'sales:messaging:internal',
    resource: 'channel-questions',
    action: 'internal',
    label: 'Sales messaging internals',
    category: 'System',
    systemOwned: true,
    operations: [
      'sales:answer-generation',
      // TV1-2259 (S7) — `BuyerInvoicePreferenceRequestPolicy`'s own event-handler POST
      // route. It reacts to the invoicing module's `BuyerInvoicePreferenceRequested` and
      // dispatches `SendChannelMessage` (D11 — the rule decides, a policy sends). Internal
      // like `answer-generation` above: never a direct user action. The SEND command it
      // dispatches keeps its own, separately grantable capability.
      'sales:buyer-invoice-preference-request',
      // TV1-2110: the `QuestionsReconcilePollingPolicy` subscription POST — an internal
      // cron-reaction seam (the dropped-webhook backstop that re-pulls received channel
      // questions through the same `syncReceivedQuestions` mechanism the manual
      // Force-Sync endpoint drives). Owned here purely to give the routing surface an
      // owner, exactly like `marketplace-health:health-sync` below.
      'sales:questions-reconcile-polling'
    ]
  },

  // ─── Operations internals (TV1-1828) ───────────────────────────────
  // Lifecycle commands dispatched by the compute/apply policies, never by a
  // human directly: record-computed-change-set (from OperationComputePolicy)
  // the apply lifecycle (start/complete/fail, from OperationApplyPolicy), and the
  // terminal compute failure (fail-operation-compute, from OperationComputePolicy).
  // PV3 exposes every aggregate command as a private route, so they must be
  // covered by a capability even though they are policy-driven.
  'operations:internal': {
    key: 'operations:internal',
    resource: 'operation-runs',
    action: 'internal',
    label: 'Operations internals',
    category: 'System',
    systemOwned: true,
    operations: [
      'operations:record-computed-change-set',
      // TV1-2113 D12: announces that a per-item PHASE of a run started, so the neutral
      // batch-processes readmodel can materialize a live progress row. Dispatched only
      // by the compute/apply policies, never by a user.
      'operations:start-operation-batch-process',
      'operations:start-operation-apply',
      'operations:complete-operation-apply',
      'operations:fail-operation-apply',
      // TV1-2135: terminal COMPUTE failure, the compute-phase sibling of
      // `fail-operation-apply`. Dispatched only by OperationComputePolicy when the
      // proposed riskClass disagrees with the registry — never by a user.
      'operations:fail-operation-compute',
      'operations:operation-runs-readmodel',
      'operations:operation-compute',
      'operations:operation-apply'
    ]
  },

  // ─── Sales channels internal ───────────────────────────────────────
  'sales-channels:webhooks:internal': {
    key: 'sales-channels:webhooks:internal',
    resource: 'sales-channel-integrations',
    action: 'webhooks',
    label: 'Sales channel webhook capture',
    category: 'System',
    systemOwned: true,
    operations: [
      'systems-integrations:capture-systems-integrations-webhook-event',
      'systems-integrations:capture-logistics-shipment-update',
      'sales:logistics-shipment-update',
      'sales:logistics-polling',
      // TV1-2082 / TV1-2095: the internal, SYSTEM-SCOPED provisioned-integrations
      // readmodel query — the generic cross-account "provisioned integrations for a
      // connector/account" query the crm/marketplace-health syncs (TV1-2095) page
      // through (no account filter, `visibility: 'internal'` — gateway-rejected,
      // in-process only). Owned here purely to give the routing surface an owner, like
      // the sibling `channel-categories-readmodel`. (TV1-2102 retired the orders
      // reconcile poll that formerly also consumed it.)
      'systems-integrations:systems-integrations-provisioned-integrations',
      // TV1-2095: the internal accessible-channels readmodel query (an assignedTo-overlap
      // self-read the identity authorization fold pages in-process; `visibility: 'internal'`,
      // gateway-rejected). Owned here purely to give the routing surface an owner, like the
      // sibling provisioned-integrations query above.
      'systems-integrations:systems-integrations-accessible-channels',
      // TV1-2099 / ADR-066: the Missed Inbound Replay policy's subscription POST. An
      // internal, cron-driven inbound gap-recovery seam that re-injects missed
      // notifications via `capture-systems-integrations-webhook-event` (above), so it
      // is owned by the same webhook-capture internal capability as that command and
      // the live `webhook-handler`.
      'systems-integrations:missed-inbound-replay',
      'systems-integrations:webhook-handler',
      'systems-integrations:mercadolibre-provisioning-from-social-login',
      'systems-integrations:channel-import-preview-cleanup',
      'systems-integrations:operation-dispatcher',
      'systems-integrations:systems-integrations-element-dispatcher',
      'systems-integrations:record-systems-integrations-operation-failure',
      'systems-integrations:record-systems-integrations-element-operation-failure',
      // TV1-2393 — the Observed Operation record written by BOTH generic dispatchers
      // when the account's tenant-behalf egress is suppressed. Owned by the same
      // internal capability as the failure record it mirrors: a policy-only write, never
      // reachable from a browser (pinned in INTERNAL_ONLY_COMMAND_ROUTES).
      'systems-integrations:record-systems-integrations-operation-observation',
      'systems-integrations:channel-categories-provision',
      'systems-integrations:channel-categories-readmodel',
      // TV1-2108 / ADR-190 — the ERP (Odoo) inbound-sync ingress. The four `capture-*`
      // commands are the versionless ERP-observation system's in-process capture seam
      // (the ERP analog of `capture-systems-integrations-webhook-event` /
      // `capture-logistics-shipment-update` above): each emits an INERT
      // `External*Observed` that only an enabled Automation rule acts on. The four
      // `odoo-*-polling` entries are the change-detecting poll policies' subscription
      // POSTs (cron-reaction seams, exactly like `sales:logistics-polling` and
      // `items-publishing:publications-reconcile-polling`); `odoo-bulk-import` is the
      // initial-catalog import policy's subscription. All internal / in-process only.
      // (`request-erp-bulk-import` left this block in ERP-ONBOARDING slice 5: it became
      // the Items tab's operator action and now lives under
      // `sales-channels:integrations:configure`.)
      'systems-integrations:capture-external-product-observed',
      'systems-integrations:capture-external-stock-observed',
      'systems-integrations:capture-external-price-observed',
      'systems-integrations:capture-external-order-observed',
      'systems-integrations:odoo-product-polling',
      'systems-integrations:odoo-stock-polling',
      'systems-integrations:odoo-price-polling',
      'systems-integrations:odoo-order-polling',
      'systems-integrations:odoo-bulk-import',
      // ERP-ONBOARDING slice 5 — the ERP catalog-import PROCESS MANAGER's subscription
      // POST. It reacts to the batch process's own `*Started` event and runs the paged,
      // chunked import; an internal reaction seam exactly like `odoo-bulk-import` above.
      'systems-integrations:erp-item-import',
      // ERP-ONBOARDING slice 9 — the ERP STOCK sweep's PROCESS MANAGER subscription POST.
      // Reacts to the batch process's own `*Started` event and runs the paged,
      // location-scoped, chunked sweep; an internal reaction seam exactly like
      // `erp-item-import` above.
      'systems-integrations:erp-stock-import',
      // ERP-ONBOARDING slice 10 — the ERP PRICE sweep's PROCESS MANAGER subscription
      // POST. Reacts to the batch process's own `*Started` event and runs the paged,
      // pricelist-scoped, chunked sweep; an internal reaction seam exactly like
      // `erp-stock-import` above.
      'systems-integrations:erp-price-import',
      // TV1-2108 / ADR-190 D18 — ERP drift evaluation is SYSTEM-driven, never a user
      // action: the internal `erp-drift-evaluation` policy reacts to either side of the
      // (record, mirror) pair moving and dispatches `evaluate-erp-drift`, which mutates
      // only the ErpSyncRecord's own open/close episode head. Exactly the shape of
      // `items-publishing:channel-drift-evaluation` / `evaluate-channel-drift`.
      'systems-integrations:evaluate-erp-drift',
      'systems-integrations:erp-drift-evaluation'
    ]
  },

  // ─── Account outbound email (TV1-2336) ─────────────────────────────
  // The account-owned outbound email primitive: an email sent through the ACCOUNT's own
  // `messaging` connector (TV1-2335), never through Teselly's shared transport. That is
  // the whole safety argument for exposing it on the rules surface at all — a badly
  // written rule risks the ACCOUNT's own domain reputation, not Teselly's shared
  // deliverability across every tenant.
  //
  // ONE capability owns BOTH operations, deliberately (TV1-2336 D30), and the reason is
  // `checkSystemPrincipalGrants.ts`, which ratchets in BOTH directions: it emits
  // `stale-system-grant` for any grant no statically-derivable policy dispatch needs,
  // and its header excludes the rules path by name (a rule graph dispatches
  // `clientLibrary.commands[X]` for an X read from a readmodel row — not derivable by
  // anyone). A rules-ONLY capability can therefore never satisfy it: adding it is RED,
  // omitting it is a silent async 403.
  //   · `record-account-email-outcome` is dispatched by OUR `AccountEmailSendingPolicy`
  //     — a real, scannable policy edge, and therefore derivable.
  //   · `send-account-email` is dispatched only by a rule graph (TV1-2337).
  // The guard resolves `command -> operation -> CAPABILITY` and records the CAPABILITY
  // in `usedSystemGrants`, so the policy's dispatch marks this entry used and the grant
  // in `systemPrincipals.ts` is green in both directions — satisfied on real evidence,
  // not silenced. The trade is stated plainly: the grant is one notch broader than a
  // hypothetical send-only grant, and a send-only grant is IMPOSSIBLE. The blast radius
  // is unchanged in practice — the capability's whole point is that a rule may send
  // email as the account.
  //
  // `systemOwned` is a GRANT-AUTHORING statement, NOT an authorization boundary, and
  // the difference matters here. It says this capability is not offered as a tick-box
  // when an operator composes a role, because there is no human-facing "send an
  // arbitrary email" screen — the intended callers are the sending policy and a rule
  // graph running under `runAsPrincipal( systemPrincipal( accountId ) )`.
  //
  // It does NOT say the route is unreachable by a tenant. `sales-channels:*` EXPANDS
  // over `systemOwned` leaves — `operationTierDerivation.test.ts` repins the operator
  // tier upward for exactly this entry — so any role holding that wildcard can POST
  // `send-account-email` directly with arbitrary text, and can POST
  // `record-account-email-outcome` (the aggregate invariant refuses a SECOND outcome,
  // not a forged FIRST one on a `requested` stream).
  //
  // That reachability is ACCEPTED, not overlooked, and it is accepted on this unit's
  // own safety argument: the transport is the ACCOUNT'S OWN SMTP server, so the blast
  // radius of the wildcard is that account's own domain reputation and its own
  // ledger — never Teselly's shared transport, which is a different port and is not
  // reachable from this route at all.
  'sales-channels:account-email:send': {
    key: 'sales-channels:account-email:send',
    resource: 'sales-channel-integrations',
    action: 'send-account-email',
    label: 'Send email as the account',
    category: 'System',
    systemOwned: true,
    sensitive: true,
    operations: [
      'systems-integrations:send-account-email',
      'systems-integrations:record-account-email-outcome',
      // TV1-2393 (D4/D5/D11, slice 4) — the Observed Operation record for a suppressed
      // account email, dispatched by the SAME `AccountEmailSendingPolicy` edge as
      // `record-account-email-outcome` above. Owned by this capability for the identical
      // reason: a real, statically-derivable policy dispatch onto the account-email
      // surface.
      //
      // Note it IS a rules-engine node: the generator turns EVERY command into one, so
      // `commandsNodes.ts` now exports `RecordAccountEmailObservationNode` (and
      // `RecordPublicationOperationObservationNode` for this slice's publications half)
      // — exactly as it already does via `SendAccountEmailNode` and
      // `RecordAccountEmailOutcomeNode` for the two operations above. That is uniform
      // generator behaviour and adds no NEW vector here, but it is worth stating
      // because `POST /rules/dry-run` dispatches REAL commands: a rule graph that
      // reaches this node writes a real Observed record. The route itself is
      // INTERNAL-ONLY (`INTERNAL_ONLY_COMMAND_ROUTES` in `generateRouteAuthConfig.ts`),
      // which is what keeps the gateway out of it; the rules surface is bounded by this
      // capability and by the aggregate, not by the node's existence.
      'systems-integrations:record-account-email-observation',
      // TV1-2336 S2 — `AccountEmailSendingPolicy`'s own subscription POST. Owned HERE
      // rather than in the webhook-capture `*:internal` capability where the other
      // systems-integrations policy subscriptions sit: that entry is described as the
      // webhook/ERP INGRESS surface, and an account-email egress seam filed under it
      // would be owned by a capability whose description does not cover it. Every
      // operation must be referenced by exactly one Capabilities entry, and this is
      // the entry that owns this unit's surface.
      'systems-integrations:account-email-sending'
    ]
  },

  // ─── Marketplace health internal ───────────────────────────────────
  // TV1-1924 / S9 + TV1-1925 / S10 — the `SyncSellerReputation` and
  // `SyncListingVisits` commands are dispatched only in-process (by the scheduled
  // `HealthSyncPolicy`, the manual Force Sync endpoint, and the shared
  // `runHealthSync` helper); neither is a user-facing operation, so — like the
  // systems-integrations `capture-*` in-process commands above — their private
  // routes are owned by an internal capability purely to give the routing surface
  // an owner. `health-sync` is the `HealthSyncPolicy` subscription POST (an internal
  // cron-reaction seam; it replaced `seller-reputation-sync` when TV1-1925
  // generalized the policy from reputation-only to a full Health Sync). The two
  // readmodels also have internal projection-subscription POSTs, but their shapes
  // are owned read-side by the user-facing `marketplace-health:reputation:read` /
  // `marketplace-health:visits:read` capabilities above.
  'marketplace-health:internal': {
    key: 'marketplace-health:internal',
    resource: 'sales-channels',
    action: 'internal',
    label: 'Marketplace health internals',
    category: 'System',
    systemOwned: true,
    operations: [
      'marketplace-health:sync-seller-reputation',
      'marketplace-health:sync-listing-visits',
      'marketplace-health:health-sync'
    ]
  },

  // ─── Stock management internal ─────────────────────────────────────
  'stock-management:internal': {
    key: 'stock-management:internal',
    resource: 'stock-availability',
    action: 'internal',
    label: 'Stock management internals',
    category: 'System',
    systemOwned: true,
    operations: [
      'stock-management:default-warehouse-creation',
      // `order-returned-restock` WAS HERE (ADR-049 OrderReturnedRestockPolicy) — deleted by
      // TV1-2327 / ADR-144: restoring stock on a reversal is now an account-configured rule.
      // KIT-STOCK-REDESIGN slice 3 (ADR-077 D9/D10): the ItemStockDerivationSyncPolicy
      // reacts to items-publishing ItemCompositionSet / de-kit and DECLARES the composite's
      // recipe on the stock side through ONE command — which also replicates the
      // kit↔component back-reference onto every COMPONENT's stock stream (the only thing a
      // stock command reads to discover the kits a moved component feeds) and announces the
      // kit's re-derived availability. Never a user; PV3 registers an endpoint per command
      // and per policy subscription regardless, so both need an entry here.
      'stock-management:item-stock-derivation-sync',
      'stock-management:record-composite-definition',
      // KIT-STOCK-REDESIGN slice 1 (ADR-077 D3/D4): a Kit's availability is recomputed by
      // its OWN command. The KitsStockAvailabilityComputePolicy reacts to a component's
      // stock event and dispatches one RecomputeKitStockAvailability per Kit named in the
      // event's `relatedBomItemsIds`. Both are INTERNAL — never a user surface — but PV3
      // registers an endpoint per command and per policy subscription regardless, and
      // generate-all's auth-surface generator is the only thing that catches a missing
      // entry, so both need one here.
      'stock-management:kits-stock-availability-compute',
      'stock-management:recompute-kit-stock-availability'
    ]
  },

  // ─── Items-publishing internal ─────────────────────────────────────
  'items-publishing:batch-callbacks:internal': {
    key: 'items-publishing:batch-callbacks:internal',
    resource: 'batch-processes',
    action: 'callbacks',
    label: 'Batch process callbacks',
    category: 'System',
    systemOwned: true,
    operations: [
      'items-publishing:publication-creation-process',
      'items-publishing:publication-item-extraction',
      // Kit Inference — PublicationKitCompositionPolicy converts a Teselly-authored
      // multi-item variation into a Composite Item + BOM. Internal only: it reacts to
      // PublicationAdded/Edited and commands its own module's aggregates, never a user.
      // (`start-publication-item-extraction` moved to `batch-processes:*` with the
      // batch-process substrate in ERP-ONBOARDING O9.)
      'items-publishing:publication-kit-composition',
      'items-publishing:publications-duplication-process',
      'items-publishing:price-list-deleted-cascade',
      'items-publishing:update-sales-channel-publication-state',
      // TV1-2085: the inbound publication-state reconcile poll's internal policy
      // (cron-driven mirror refresh — re-reads synced publications by channel id and
      // dispatches UpdateSalesChannelPublicationState, above). Internal, like the
      // webhook-dispatched sync command it re-uses.
      'items-publishing:publications-reconcile-polling',
      // TV1-2105 (Observation Mode, D5, slice 7): drift evaluation is system-driven
      // — the internal `channel-drift-evaluation` policy reacts to a desired/mirror
      // change and dispatches the `evaluate-channel-drift` command, which mutates the
      // publication's own open/close drift state (a publication update, like
      // `update-sales-channel-publication-state` above). Never a direct user action.
      'items-publishing:evaluate-channel-drift',
      'items-publishing:channel-drift-evaluation',
      // TV1-2113 S10: the internal policy that re-resolves the publications sitting in
      // a channel category the seller just mapped. System-driven — it reacts to
      // `ChannelCategoryMapped` and is never a direct user action, so it belongs with
      // the other internal reaction policies rather than with a user-facing capability.
      'items-publishing:recategorize-publications',
      // TV1-2120 (D4 / ADR-060 A1.5): the ItemDeleted cascade — delist every publication
      // advertising a deleted item, THEN retire its stock. System-driven and never a
      // direct user action: the USER-facing gate is the `delete-items` operation's
      // preflight + approval token (D-A1-2) and the `items-publishing:items:delete`
      // capability it declares; this route is only the reaction that follows a committed
      // ItemDeleted, exactly like `price-list-deleted-cascade` above.
      'items-publishing:item-delete-cascade',
      // TV1-2120 (D-A1-1): on a connector whose relist MINTS a new listing (MercadoLibre
      // — a closed item can never be reactivated), reversing a delist creates a NEW
      // Publication for the new listing. System-driven and never a direct user action:
      // the USER-facing gate is `items-publishing:relist-publication` under
      // `publications:delete`; this is only the reaction that follows the committed
      // request, exactly like `price-list-deleted-cascade` above.
      'items-publishing:publication-republish',
      'items-publishing:stream'
    ]
  },

  // ─── Invoicing internal ────────────────────────────────────────────
  'invoicing:internal': {
    key: 'invoicing:internal',
    resource: 'invoices',
    action: 'internal',
    label: 'Invoicing internals',
    category: 'System',
    systemOwned: true,
    operations: [
      'invoicing:attach-fiscal-document-pdf',
      'invoicing:fiscal-document-pdf-generation',
      'invoicing:retail-store-invoice-generation',
      'invoicing:fiscal-document-issuance-dispatcher',
      'invoicing:confirm-fiscal-document-authorization',
      'invoicing:record-fiscal-document-issuance-failure',
      'invoicing:record-fiscal-document-issuance-observation',
      // TV1-1969 (ADR-053): the serial-dispatch topology's internal ops.
      'invoicing:fiscal-document-issuance-bridge',
      'invoicing:enqueue-fiscal-document-issuance',
      'invoicing:assign-fiscal-document-number',
      // TV1-1969 slice 3: 10016 non-correlative renumber (write corrected number ahead).
      'invoicing:reassign-fiscal-document-number',
      // ADR-049 / S6: CreditNoteIssuanceOnReversalPolicy reacting to OrderReturned/OrderCancelled.
      'invoicing:credit-note-issuance-on-reversal',
      // TV1-2207 (S5b): the two internal policies that carry a note from money
      // REQUEST to fiscal DOCUMENT and back to the CONFIRMED money fact.
      // `fiscal-note-issuance` reacts to CreditNoteRequested/DebitNoteRequested
      // and dispatches IssueFiscalNote on the note's own stream;
      // `fiscal-note-authorization` reacts to the note document's
      // FiscalDocumentAuthorized and dispatches ConfirmCreditNote/ConfirmDebitNote.
      // Both are reactions to committed facts, never direct user actions — the
      // user-facing gate is `invoicing:fiscal-documents:generate` on the commands that
      // START the lane.
      'invoicing:fiscal-note-issuance',
      'invoicing:fiscal-note-authorization',
      // TV1-2207 (S5a) — the CONFIRM half of the note money model. Dispatched
      // when the note's fiscal document is authorized, exactly like
      // `invoicing:confirm-fiscal-document-authorization` above, never by a user.
      'invoicing:confirm-credit-note',
      'invoicing:confirm-debit-note',
      // TV1-2078 relocated SubscriptionInvoiceFiscalInvoicePolicy billing → invoicing (the
      // policy file now lives in `modules/invoicing/`), so its internal route derives the
      // `invoicing` subdomain. Moved here from `billing:internal` to match.
      'invoicing:subscription-invoice-fiscal-invoice',
      // TV1-2259 (S8) — the BUYER-QUESTION TIMEBOX, all six internal.
      //
      // Both COMMANDS are dispatched only by this lane's own policies (the cron sweep and
      // the channel-refusal reaction), never by a user, exactly like
      // `invoicing:confirm-fiscal-document-authorization` above. Note this is a DIFFERENT
      // filing from S7's `invoicing:request-buyer-invoice-preference`, which sits on
      // `invoicing:fiscal-documents:generate` because a seller's RULE dispatches it through
      // the client library — that one has a human-authored trigger; these two do not.
      'invoicing:resolve-buyer-invoice-timeout',
      'invoicing:clear-buyer-invoice-preference-request',
      // The three artifacts: the nomination projection, the cron sweep that reads it, and
      // the policy that issues the `issueDefault` document off the recorded resolution.
      'invoicing:buyer-invoice-preferences-readmodel',
      // ...and the READ route the projection declares explicitly for its tenant filter (the
      // operation key is derived from the ROUTE, so it is a second key for the same
      // artifact). Filed as system-owned deliberately: the rows are fiscal decision material
      // with no seller-facing screen behind them yet, so there is no user-facing capability to
      // put it on. Giving it one is a product decision, exactly like the S7 note above.
      'invoicing:buyer-invoice-preferences',
      'invoicing:buyer-invoice-preference-timeout-sweep',
      'invoicing:buyer-invoice-preference-timeout-issuance',
      'invoicing:buyer-invoice-preference-clearing'
    ]
  },

  // ─── Automation internal ───────────────────────────────────────────
  'automation:internal': {
    key: 'automation:internal',
    resource: 'rule-executions',
    action: 'internal',
    label: 'Automation internals',
    category: 'System',
    systemOwned: true,
    operations: [
      'automation:start-rule-execution',
      'automation:complete-rule-execution',
      'automation:fail-rule-execution',
      'automation:claim-rule-execution',
      // TV1-2078 relocated RulesExecutionFailureNotificationPolicy automation → notifications;
      // its internal route's operation key is therefore now `notifications:...` (see the
      // notifications:internal block). Removed here to reconcile the stale registry with the
      // regenerated route-config (unblocks TV1-2081's authorization-surface generation).
      'automation:order-status-changed-rules-execution',
      // TV1-2327 (ADR-144): the standalone policy that makes OrderCancelled / OrderReturned
      // rules triggers — same class of system-owned internal route as its status sibling.
      'automation:order-reversal-rules-execution',
      'automation:pricelist-rules-execution',
      'automation:publication-created-rules-execution',
      'automation:stock-availability-updated-rules-execution',
      // TV1-2229 / ADR-131: makes `WarehouseItemOversold` a rules trigger, so an operator
      // can author a rule that reacts to a sale that outran its stock.
      'automation:warehouse-item-oversold-rules-execution',
      'automation:warehouse-selection-rules-execution',
      // TV1-2073: SuspensionResumeChannelReconciliationPolicy re-derives the account's
      // rule-governed channel publications (stock/price) on resume, reacting to
      // ClientAccountResumed and re-driving the TV1-2081 primitive per notSynced pub.
      'automation:suspension-resume-channel-reconciliation',
      // TV1-2446: CredentialRestorationChannelCatchUpPolicy — on a restored integration
      // credential, starts the revoked window's order import, then (on that batch's
      // completion or failure) re-drives the integration's notSynced publications.
      'automation:credential-restoration-channel-catch-up',
      // TV1-2108 / ADR-190: the ERP Entity Sync rule-execution producers. Four INBOUND
      // producers select & dispatch the `builtIn-upsert-teselly-*-from-erp` rules off the
      // inert `External*Observed` observations, and four OUTBOUND producers dispatch the
      // `builtIn-upsert-erp-*-from-teselly` rules off Teselly's own item/stock/price/order
      // events (injecting the target ERP connector coordinate). Internal, cron/outbox-driven
      // — same class as the sibling `*-rules-execution` producers above.
      'automation:erp-product-observed-rules-execution',
      'automation:erp-stock-observed-rules-execution',
      'automation:erp-price-observed-rules-execution',
      'automation:erp-order-observed-rules-execution',
      'automation:item-events-rules-execution',
      'automation:stock-events-erp-rules-execution',
      'automation:price-events-erp-rules-execution',
      'automation:order-events-erp-rules-execution',
      // TV1-2259 (S10) — the TWO rules-execution producers of the buyer-invoice-reply lane,
      // same class as every `*-rules-execution` sibling above: an internal event-handler POST
      // route, never a user action.
      //
      // ⚠️ THEY ARE TWO ENTRIES BECAUSE THEY ARE TWO POLICIES, AND THAT SPLIT IS R3, NOT
      // BOOKKEEPING. `buyer-invoice-reply-classification` dispatches the rule holding the
      // paid `LlmClosedQuestion` node; `buyer-invoice-intent` dispatches the purely
      // deterministic issuance rule. Merged into one policy/rule, a retry of the issuance
      // would restart the graph at node 0 and RE-CHARGE the model call.
      'automation:buyer-invoice-reply-classification-rules-execution',
      'automation:buyer-invoice-intent-rules-execution',
      // TV1-2105 (Observation Mode): two account-mode reconcile policies re-drive the
      // TV1-2081 primitive per publication — priming seeds Desired Channel State on
      // AccountEnteredObservation (whole catalog), GoLive re-drives notSynced on cutover.
      // Internal system routes (mirrors suspension-resume-channel-reconciliation above).
      'automation:enter-observation-channel-priming',
      'automation:observation-go-live-reconciliation',
      // TV1-2132 (design D8): the FOURTH consumer of the TV1-2081 primitive. On
      // BatchProcessCompleted for an ERP STOCK run it re-drives each affected publication
      // ONCE, closing the loop D7 opened by suppressing the sweep's per-row channel
      // pushes. Internal system route, same class as the three reconciles above.
      'automation:erp-stock-import-channel-reconciliation',
      // TV1-2392 slice 4 (D7): StockThresholdAccountDefaultPolicy — seeds one
      // account-level umbral de stock on AccountRegistered. Internal event-handler
      // route, same class as every `*-rules-execution`/reconciliation sibling above.
      'automation:stock-threshold-account-default'
    ]
  },

  // ─── Notifications internal ────────────────────────────────────────
  'notifications:internal': {
    key: 'notifications:internal',
    resource: 'notifications',
    action: 'internal',
    label: 'Notifications internals',
    category: 'System',
    systemOwned: true,
    operations: [
      'notifications:publication-batch-operation-completion-notification',
      // TV1-2078 relocated RulesExecutionFailureNotificationPolicy automation → notifications
      // (the policy file now lives in `modules/notifications/`), so its internal route derives
      // the `notifications` subdomain. Moved here from `automation:internal` to match.
      'notifications:rules-execution-failure-notification',
      // TV1-2229 / ADR-131: the operator signal for an oversell. Sits beside the
      // rules-execution failure notification because the two are the same kind of thing —
      // and because they are now complementary: since the sale path clamps instead of
      // throwing, an oversell raises THIS notification where it used to raise that one.
      'notifications:oversold-notification',
      // TV1-2386: the AutomationRequest Sub-Goal transition notifications (epic TV1-2377).
      // `systemOwned` like its neighbours — the trigger is the outbox, never a caller, and
      // nothing about this operation is a tenancy decision (the recipient is carried on the
      // event as `requestedByUserId`, not derived from whoever holds the capability).
      'notifications:automation-request-notification'
    ]
  },

  // ─── ML & AI internal ──────────────────────────────────────────────
  'ml-and-ai:agents:callbacks': {
    key: 'ml-and-ai:agents:callbacks',
    resource: 'agent-runs',
    action: 'callbacks',
    label: 'AI agent callbacks',
    category: 'System',
    systemOwned: true,
    operations: [
      'ml-and-ai:agent-runs',
      'ml-and-ai:ai-assistant-user-message-processing',
      'ml-and-ai:ai-assistant-response-persistence',
      'ml-and-ai:conversations-readmodel'
    ]
  },

  //*******************************************
  // Wildcard capabilities (SDD §4.4) — user-assignable
  //*******************************************
  // Per SDD §4.4, wildcard capability keys (e.g. `accounting:*`) are
  // EXPLICITLY registered (not synthesized) so that `Grant.capability:
  // CapabilityKey` accepts them at the type level. This is required by the
  // system roles defined in ticket 9 (account-owner, manager, operator),
  // which grant whole subdomains via wildcards.
  //
  // - `operations: []` — wildcards never contribute their own operations;
  //   the resolver expands them by walking the leaf capabilities returned
  //   from `matchCapabilities(key)`. The wildcard entry itself is excluded
  //   from that result by the `c.key !== key` self-exclusion in
  //   matchCapabilities.ts.
  // - `resource` — informational only (SDD §4.4). Chosen as the umbrella
  //   resource for each subdomain's most-referenced leaf. Aggregate R5
  //   validates scopes against EACH matched leaf's resource, not the
  //   wildcard's own resource.
  // - `sensitive` — set to true when any leaf in the subdomain is sensitive,
  //   so the admin UI flags wildcards that pull in sensitive caps.
  // - `category: 'Bundles'` — keeps wildcards visually distinct in the role
  //   builder per Open Question Q3 default in the SDD.
  // - `systemOwned: false` — wildcards are user-assignable. SYSTEM-OWNED LEAVES
  //   matched by a wildcard are still filtered at grant validation by R2, which
  //   rejects a wildcard only when EVERY leaf it matches is system-owned.
  //   (Renamed from `internal` by TV1-1535 — see the note at the top of this file.)

  'identity:*': {
    key: 'identity:*',
    resource: 'auth-users',
    action: 'manage',
    label: 'All Identity capabilities',
    category: 'Bundles',
    sensitive: true,
    systemOwned: false,
    operations: []
  },

  'clients-management:*': {
    key: 'clients-management:*',
    resource: 'accounts',
    action: 'manage',
    label: 'All Clients Management capabilities',
    category: 'Bundles',
    sensitive: true,
    systemOwned: false,
    operations: []
  },

  'accounting:*': {
    key: 'accounting:*',
    resource: 'chart-of-accounts',
    action: 'manage',
    label: 'All Accounting capabilities',
    category: 'Bundles',
    sensitive: true,
    systemOwned: false,
    operations: []
  },

  'billing:usage:read': {
    key: 'billing:usage:read',
    resource: 'billing-entities',
    action: 'read',
    label: 'Read billing usage',
    category: 'Billing',
    operations: [
      'billing:aggregate',
      'billing:publications',
      'billing:sales'
    ]
  },
  'billing:internal': {
    key: 'billing:internal',
    resource: 'billing-entities',
    action: 'internal',
    label: 'Billing internals',
    category: 'System',
    sensitive: true,
    systemOwned: true,
    operations: [
      // TV1-1752 (S1c, ADR-130) — the forced-termination DRY RUN,
      // `GET /billing/account-termination-preview/:clientAccountId`. A GET on a
      // non-readmodel route derives its key from the LAST NON-PARAM PATH SEGMENT
      // (ADR-057), so renaming that segment orphans this entry.
      //
      // Owned by `billing:internal` — `systemOwned: true`, so NO user-defined custom
      // role can ever be granted it (`validateGrants.ts` R2). It is the most
      // restrictive home available: the route narrates the consequences of destroying
      // an account, and every private operation must be owned by EXACTLY ONE
      // capability or `generate-operation-capability` fails the build, so "owned by
      // nothing" is not an option.
      'billing:account-termination-preview',
      // TV1-1752 (S3b / S4) — the two BILLING reactions to `ClientAccountCancelled`:
      // `BillingAccountCancelationPolicy` (→ CancelAllBillingContracts) and
      // `TerminationDunningAbortPolicy` (→ AbortDunningProcess). Internal policy
      // routes, never user-facing, and deliberately homed on a `systemOwned`
      // capability so no user-defined role can ever be granted them — a tenant able
      // to drive its own termination cascade is exactly what this PR must not create.
      'billing:billing-account-cancelation',
      'billing:billing-account-registering',
      'billing:client-account-registered',
      'billing:contract-cancellation-proration',
      'billing:contract-renewal',
      'billing:debit-payment-unapplying',
      'billing:dunning-advancement-cron',
      'billing:dunning-payment-collection',
      'billing:dunning-process-execution',
      'billing:gateway-charge-callback',
      'billing:gateway-reconciliation',
      'billing:gateway-refund-callback',
      'billing:immediate-subscription-invoice-run',
      'billing:subscription-invoice-created-email-sending',
      // TV1-2078 relocated SubscriptionInvoiceFiscalInvoicePolicy billing → invoicing; its
      // internal route's operation key is therefore now `invoicing:...` (see invoicing:internal).
      // Removed here to reconcile the stale registry with the regenerated route-config.
      'billing:subscription-invoice-generation',
      'billing:subscription-invoice-past-due-sweep',
      'billing:subscription-invoice-payment-unapplying',
      'billing:subscription-invoice-posting',
      'billing:subscription-invoice-settled-standing',
      // TV1-1752 (S4, ADR-129) — see the note on `billing:billing-account-cancelation`.
      'billing:termination-dunning-abort',
      'billing:net-zero-subscription-invoice-settlement',
      // TV1-2241 — the mirror of the policy above: it walks a net-zero account's
      // outstanding INVOICES when a CREDIT is created, closing the "suspended with no
      // automatic escape" path where creating the covering credit released nothing.
      'billing:credit-top-up-settlement',
      // TV1-2241 — hourly cron; cancels draft payments whose billing_collection_lock was
      // orphaned by a crash between CreateDraftPayment and PublishPayment (ADR-038 Am. 2).
      'billing:stale-collection-sweep',
      'billing:payment-application',
      'billing:payment-collection',
      'billing:payment-method-changed-payment-retrying',
      'billing:payment-overdue-email-sending',
      'billing:payment-reversing',
      'billing:rate-plan-migration',
      'billing:recurring-subscription-invoice-run',
      'billing:recurring-payment-run',
      'billing:refund-issuance',
      'billing:trial-products-association',
      'billing:usage-publications-readmodel',
      'billing:usage-sales-readmodel'
    ]
  },
  'billing:*': {
    key: 'billing:*',
    resource: 'billing-products',
    action: 'manage',
    label: 'All Billing capabilities',
    category: 'Bundles',
    sensitive: true,
    systemOwned: false,
    operations: []
  },

  'invoicing:*': {
    key: 'invoicing:*',
    resource: 'billing-entities',
    action: 'manage',
    label: 'All Invoicing capabilities',
    category: 'Bundles',
    sensitive: true,
    systemOwned: false,
    operations: []
  },

  'items-publishing:*': {
    key: 'items-publishing:*',
    resource: 'items',
    action: 'manage',
    label: 'All Catalog (items-publishing) capabilities',
    category: 'Bundles',
    sensitive: true,
    systemOwned: false,
    operations: []
  },

  'sales:*': {
    key: 'sales:*',
    resource: 'orders',
    action: 'manage',
    label: 'All Sales capabilities',
    category: 'Bundles',
    systemOwned: false,
    operations: []
  },

  // TV1-2158 — the `procurement` subdomain bundle. Required for the wildcard to
  // exist AT ALL: `ALL_SUBDOMAIN_WILDCARDS` is typed `readonly CapabilityKey[]`,
  // and `matchCapabilities` expands a wildcard by PREFIX over the live registry.
  'procurement:*': {
    key: 'procurement:*',
    resource: 'suppliers',
    action: 'manage',
    label: 'All Procurement capabilities',
    category: 'Bundles',
    systemOwned: false,
    operations: []
  },

  'stock-management:*': {
    key: 'stock-management:*',
    resource: 'stock-availability',
    action: 'manage',
    label: 'All Stock Management capabilities',
    category: 'Bundles',
    sensitive: true,
    systemOwned: false,
    operations: []
  },

  'sales-channels:*': {
    key: 'sales-channels:*',
    resource: 'sales-channel-integrations',
    action: 'manage',
    label: 'All Sales Channels Integrations capabilities',
    category: 'Bundles',
    sensitive: true,
    systemOwned: false,
    operations: []
  },

  'automation:*': {
    key: 'automation:*',
    resource: 'rules',
    action: 'manage',
    label: 'All Automation capabilities',
    category: 'Bundles',
    sensitive: true,
    systemOwned: false,
    operations: []
  },

  'batch-processes:*': {
    key: 'batch-processes:*',
    resource: 'batch-processes',
    action: 'manage',
    label: 'All Batch Processes capabilities',
    category: 'Bundles',
    systemOwned: false,
    operations: []
  },

  'analytics:*': {
    key: 'analytics:*',
    resource: 'analytics-layouts',
    action: 'manage',
    label: 'All Analytics capabilities',
    category: 'Bundles',
    systemOwned: false,
    operations: []
  },

  // TV1-1924 / S9 follow-up. Anchors on the tenant-global `sales-channels`
  // resource, like every other marketplace-health capability. Deliberately NOT
  // added to `ALL_SUBDOMAIN_WILDCARDS` (roles-and-permissions.ts) — that list is
  // what manager/operator inherit from; this bundle is granted to account-owner
  // ONLY for now, until the subdomain's role model is decided.
  'marketplace-health:*': {
    key: 'marketplace-health:*',
    resource: 'sales-channels',
    action: 'manage',
    label: 'All Marketplace Health capabilities',
    category: 'Bundles',
    systemOwned: false,
    operations: []
  },

  'notifications:*': {
    key: 'notifications:*',
    resource: 'notifications',
    action: 'manage',
    label: 'All Notifications capabilities',
    category: 'Bundles',
    sensitive: true,
    systemOwned: false,
    operations: []
  },

  'shipping:*': {
    key: 'shipping:*',
    resource: 'shipping-carriers',
    action: 'manage',
    label: 'All Shipping capabilities',
    category: 'Bundles',
    systemOwned: false,
    operations: []
  },

  'feature-flags:*': {
    key: 'feature-flags:*',
    resource: 'feature-flags',
    action: 'manage',
    label: 'All Feature Flags capabilities',
    category: 'Bundles',
    sensitive: true,
    systemOwned: false,
    operations: []
  },

  'sales:messaging:*': {
    key: 'sales:messaging:*',
    resource: 'channel-questions',
    action: 'manage',
    label: 'All Sales messaging capabilities',
    category: 'Bundles',
    systemOwned: false,
    operations: []
  },

  'operations:*': {
    key: 'operations:*',
    resource: 'operation-runs',
    action: 'manage',
    label: 'All Operations capabilities',
    category: 'Bundles',
    systemOwned: false,
    operations: []
  },

  'ml-and-ai:*': {
    key: 'ml-and-ai:*',
    resource: 'conversations',
    action: 'manage',
    label: 'All ML & AI capabilities',
    category: 'Bundles',
    systemOwned: false,
    operations: []
  },

  'ml-and-ai:llm:internal': {
    key: 'ml-and-ai:llm:internal',
    resource: 'agent-runs',
    action: 'llm-internal',
    label: 'LLM internals',
    category: 'System',
    systemOwned: true,
    operations: [
      'ml-and-ai:llm-store-generated-embeddings',
      'ml-and-ai:llm-store-generated-json-from-schema',
      'ml-and-ai:llm-store-generated-json-from-schema-with-reasoning',
      'ml-and-ai:llm-store-generated-text',
      'ml-and-ai:llm-store-generated-text-with-reasoning',
      'ml-and-ai:serper-web-search-used'
    ]
  },

  // ─── Feature flags internal ────────────────────────────────────────
  'feature-flags:internal': {
    key: 'feature-flags:internal',
    resource: 'feature-flags',
    action: 'internal',
    label: 'Feature flags internals',
    category: 'System',
    systemOwned: true,
    operations: [
      'feature-flags:feature-flag-scheduling',
      // TV1-2095: the `/feature-flags-by-key` global get-by-key lookup —
      // `visibility: 'internal'`, reached in-process (via `ports.endpoints.fetch`)
      // by the pre-auth passkey/one-tap login command handlers (no `context.user`).
      // Owned here purely to give the routing surface an owner; not user-grantable.
      'feature-flags:feature-flags-by-key'
    ]
  },

  // ─── Generic (admin / infra) ───────────────────────────────────────
  //
  // The pv3 ADMIN-PANEL operations that used to sit here (`session`, `config`,
  // `manifest.json`, `cluster-structure`, and the outbox-plugin verbs) are gone
  // deliberately: `setupAdminServices.ts` now registers the panel with an EMPTY
  // subdomain, so those routes take the gateway's admin branch (authenticated +
  // `userType === 'admin'`) and never reach a capability check. A capability
  // naming an operation route-config no longer emits is drift, and
  // `generate-capability-registry` fails the build on it.
  //
  // What remains are the operations still registered UNDER the `generic`
  // subdomain — cronjob subscriptions and uploads — which are still org-scoped
  // and still gated here, genuinely admin/infra-grade (outbox-drain, cronjob
  // tick, idempotency GC).
  'generic:admin:infra': {
    key: 'generic:admin:infra',
    resource: 'cronjobs',
    action: 'admin-infra',
    label: 'Admin / infra endpoints',
    category: 'System',
    systemOwned: true,
    operations: [
      'generic:cronjob-subscriptions-readmodel',
      'generic:errored-streams',
      'generic:idempotency-checkpoint-gc',
      'generic:register-cronjob-subscription',
      'generic:stream',
      'generic:subscriptions',
      'generic:tick-cronjob',
      'generic:unregister-cronjob-subscription',
      'generic:upload-file',
      'generic:upload-image'
    ]
  },

  // TV1-2446 — Credential State: `MarkIntegrationCredentialRevoked` /
  // `MarkIntegrationCredentialRestored` on the Integration aggregate.
  //
  // No BUILT-IN role holds it, and that is what the `generic:` prefix buys: it is the one
  // capability prefix no tenant role wildcards (`ALL_SUBDOMAIN_WILDCARDS` in
  // `roles-and-permissions.ts` never lists `generic:*`). A tenant's own CUSTOM role can
  // still expand over it by granting `generic:*` explicitly — the aggregate's
  // `requireCallerOwnsIntegration()` invariant then confines that caller to integrations
  // it owns. The routes stay PRIVATE (not internal-only) so the blanket admin bypass
  // reaches them too — the operator escape hatch for a false flag on a seller nobody
  // can reach.
  //
  // Filing them under `sales-channels:webhooks:internal` was WRONG: every tenant role
  // expands `sales-channels:*` over that systemOwned leaf, so any account-owner could revoke
  // another tenant's integration by naming its id (and silence its channel pushes). The
  // aggregate now ALSO binds the caller to the integration's account
  // (`requireCallerOwnsIntegration`), so the capability is not the only line.
  //
  // GRANTED to the system principal (`SYSTEM_PRINCIPAL_CAPABILITIES`) since the Credential State
  // port (`systems-integrations/channelCredentialState.port.ts`) dispatches both commands as
  // `system@<accountId>`; the grant guard roots that `*.port.ts` file, and ratchets both ways.
  'generic:admin:integration-credential-state': {
    key: 'generic:admin:integration-credential-state',
    resource: 'sales-channel-integrations',
    action: 'admin-credential-state',
    label: 'Mark integration credential revoked / restored (platform admin)',
    category: 'System',
    systemOwned: true,
    operations: [
      'systems-integrations:mark-integration-credential-revoked',
      'systems-integrations:mark-integration-credential-restored'
    ]
  },

  //*******************************************
  // Procurement (TV1-2158)
  //*******************************************
  // The Supplier master. Its resource is TENANT-GLOBAL (`all` only): a Supplier
  // belongs to exactly ONE account, is never shared across accounts and carries
  // no channel/principal anchor, so the account boundary IS the access model —
  // same shape as `customers` / `billing-entities`.
  // ⚠️ A capability key that no role lists is granted to NOBODY and fails as a
  // SILENT 403 — the grants live in
  // `modules/identity/teselly-better-auth/authorization/roles-and-permissions.ts`
  // (`procurement:*` in `ALL_SUBDOMAIN_WILDCARDS`, plus the operator re-grant),
  // and are test-asserted there.

  'procurement:suppliers:read': {
    key: 'procurement:suppliers:read',
    resource: 'suppliers',
    action: 'read',
    label: 'Read suppliers',
    category: 'Procurement',
    operations: [
      // The published readmodel query (`/procurement/suppliers/:supplierId?`) —
      // the contract sibling units resolve a supplierId through.
      'procurement:suppliers',
      'procurement:suppliers-readmodel',
      // TV1-2330 — the published item-sourcing query
      // (`/procurement/item-sourcing/:itemId?`): which supplier sells this item,
      // under which code, and which one we prefer. Filed on the SUPPLIERS
      // capability rather than a new `item-sourcing` resource: it is the
      // supplier catalog by another name — same audience, same account-only
      // scope — and a new resource would need role-grant plumbing across every
      // tier for no change in who may read it.
      'procurement:item-sourcing',
      'procurement:item-sourcing-readmodel'
    ]
  },

  'procurement:suppliers:manage': {
    key: 'procurement:suppliers:manage',
    resource: 'suppliers',
    action: 'manage',
    label: 'Manage suppliers',
    category: 'Procurement',
    operations: [
      'procurement:register-supplier',
      'procurement:edit-supplier',
      'procurement:deactivate-supplier',
      'procurement:reactivate-supplier',
      // TV1-2330 — the ItemSourcing write surface. Same capability, same
      // reasoning as the read side above: naming where an item is bought from is
      // supplier master-data maintenance, done by whoever maintains suppliers.
      'procurement:map-supplier-sku',
      'procurement:unmap-supplier-sku',
      'procurement:set-preferred-supplier',
      'procurement:clear-preferred-supplier'
    ]
  },

  // ── Purchase orders (TV1-2333) ─────────────────────────────────────────────
  // A commitment to BUY, issued by one account to one supplier. Same
  // tenant-global (`all`-only) resource shape as `suppliers`: no per-row owner
  // exists in wave 1 (see `resources.ts`).
  // ⚠️ `procurement:purchase-orders:manage` is NOT an operator-only surface: it
  // gates `PlacePurchaseOrder`, which TV1-2337's built-in rule A dispatches
  // under `runAsPrincipal( systemPrincipal( accountId ))`. That is why this key
  // — and no other procurement key — also appears in
  // `SYSTEM_PRINCIPAL_CAPABILITIES`. Removing it from either place produces an
  // ASYNC 403 in the rules worker that no gate catches.
  // ⚠️ No `operator` re-grant, unlike `procurement:suppliers:read`: that grant
  // exists to fix a name-resolution collapse (an operator seeing a bare
  // supplierId). There is no operator-facing purchase-order surface in wave 1,
  // so a grant here would be a DEAD grant and `deadGrantGate.test.ts` pins it.

  'procurement:purchase-orders:read': {
    key: 'procurement:purchase-orders:read',
    resource: 'purchase-orders',
    action: 'read',
    label: 'Read purchase orders',
    category: 'Procurement',
    operations: [
      // The published readmodel query
      // (`GET /procurement/purchase-orders/:purchaseOrderId?`) and the internal
      // `POST /purchase-orders-readmodel` route the query rides on — the exact
      // pair `procurement:suppliers:read` lists above.
      'procurement:purchase-orders',
      'procurement:purchase-orders-readmodel'
    ]
  },

  'procurement:purchase-orders:manage': {
    key: 'procurement:purchase-orders:manage',
    resource: 'purchase-orders',
    action: 'manage',
    label: 'Manage purchase orders',
    category: 'Procurement',
    operations: [
      'procurement:place-purchase-order',
      'procurement:cancel-purchase-order'
    ]
  },

  // `generic:realtime-session` used to live inside `generic:admin:infra` above
  // (`systemOwned: true`) — but it is NOT an admin/infra operation: it is the
  // single WebSocket channel every PV3 realtime SUBSCRIPTION in the app rides
  // on (readmodel live-updates, the admin panel's own list views), opened by
  // every authenticated tenant member on login, not just admins. Because no
  // system role (`account-owner` included — `ALL_SUBDOMAIN_WILDCARDS` never
  // listed `generic:*`) was ever granted `generic:admin:infra`, EVERY
  // `GET /realtime-session` request from EVERY tenant failed
  // `checkPermission` with a 403, in every environment — a total, silent
  // regression of every realtime feature masquerading as a browser CORS
  // failure (a rejected upgrade handshake reports as `1006`/no readable
  // status client-side). Split out, `systemOwned: false`, and granted as a
  // baseline self-capability to every role — same shape as
  // `identity:self:read` / `feature-flags:self:read` below.
  'generic:self:realtime': {
    key: 'generic:self:realtime',
    resource: 'cronjobs',
    action: 'read-self',
    label: 'Open own realtime session',
    category: 'Operations',
    systemOwned: false,
    operations: [
      'generic:realtime-session'
    ]
  }

} as const satisfies Record<string, CapabilityShape>;

export type CapabilityKey = keyof typeof Capabilities;

// Public `Capability` type with a strongly-typed `implies` field. Declared
// after `Capabilities` and `CapabilityKey` to sidestep the circular reference
// that prevents `implies?: readonly CapabilityKey[]` from being placed inside
// the `satisfies` constraint above.
export type Capability = Omit<CapabilityShape, 'implies'> & {
  implies?: readonly CapabilityKey[];
};

//*******************************************
// Operation coverage cross-check (manual, ticket 2)
//*******************************************
// As of TV1-1782 ticket 2 the route-config.json was extracted and verified:
// every one of the 404 (subdomain:operation) pairs is referenced by exactly
// one Capabilities entry above. The build-time generator in ticket 16
// (`scripts/generate-capability-registry.ts`) will mechanize this check.
//
// Orphan operations as of this commit: none.
// Doubly-referenced operations as of this commit: none.
//
// Intentionally excluded from capability coverage:
//   The no-subdomain dev-utils routes registered under `/dev-utils/*`
//   in `src/services/server/src/modules/identity/route-config.json`:
//     - `echo-auth-headers`
//     - `events`
//   These are dev-only routes (mounted only in non-production environments
//   for debugging / e2e teardown) and are NOT part of the production
//   authorization surface. They have no subdomain, no operation key in the
//   capability-tier sense, and must not be wrapped under any user-facing
//   Capability. Ticket 16's CI drift script
//   (`scripts/generate-capability-registry.ts`) MUST honor this exclusion
//   by skipping route entries whose `subdomain` is absent / falsy or whose
//   path matches `/dev-utils/*`. Treating them as orphans would force us to
//   either invent a phantom "dev-utils" Capability (leaking dev semantics
//   into the production authz model) or grant them under `generic:*`
//   (incorrectly implying they're admin-infra production endpoints).

export { matchCapabilities } from './matchCapabilities';
